/**
 * AI CONTEXT: admin-dashboard - Administrative control panel
 * Journal: J-20250809-01, J-20250817-01
 * 
 * This component implements the admin dashboard with:
 * - Mobile-first responsive design
 * - Comprehensive admin functionality
 * - Cost monitoring and analytics
 * - Giveaway management system
 * - User data management
 * 
 * Architecture decisions:
 * - Use tabbed interface for organization
 * - Implement real-time cost monitoring
 * - Provide comprehensive admin controls
 * - Follow mobile-first design principles
 * 
 * Future considerations:
 * - Add advanced analytics dashboard
 * - Implement real-time notifications
 * - Add bulk operations for efficiency
 * - Consider role-based access control
 */

import React, { useState, useEffect, useId, useCallback } from 'react';
import { t } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { useGiveaway } from '../contexts/GiveawayContext';
import { useProducts } from '../contexts/ProductContext';
import CostMonitor from '../components/CostMonitor';
import S3Image from '../components/S3Image';
import databaseService from '../services/database';
import authService from '../services/auth';
// getAssetUrl not needed where S3Image handles fallbacks
import { uploadProductImage, uploadGiveawayImage, isStorageConfigured } from '../services/storage';
import { isApiConfigured, authorizedFetch, getUserProfile, getAdminAllReferrals, backfillReferral, getAdminAcquisitionRequests, updateAdminAcquisitionRequest } from '../services/apiClient';
import { formatDateTime, calculateEarlyAccessDate } from '../utils/date';
import { 
  Users, 
  Gift, 
  Plus, 
  Minus,
  Edit, 
  Trash2, 
  Download,
  DollarSign,
  TrendingUp,
  Shield,
  Database,
  RefreshCw,
  ShoppingBag,
  Eye,
  MessageSquare,
  MapPin,
  AlertCircle,
  HelpCircle,
  Search,
  UserPlus,
  FileText,
  Crosshair
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminSmsModal from '../components/AdminSmsModal';
import AdminBlogTab from '../components/AdminBlogTab';
import UserCreationModal from '../components/UserCreationModal';
import WinnerPickerModal from '../components/WinnerPickerModal';
import { creditService } from '../services/creditService';

const formatDate = (dateString, options = {}) =>
  formatDateTime(dateString, { includeTime: true, ...options });

const AdminProductForm = ({ initialValues, onCancel, onSave }) => {
  const productInputId = useId();
  const productMultiInputId = useId();
  const { getAvailableDisplayOrders } = useProducts();
  const [form, setForm] = useState(() => ({
    name: initialValues?.name || '',
    // Store numeric fields as strings so inputs can be cleared; convert on save
    price: initialValues?.price !== undefined ? String(initialValues.price) : '',
    salesPrice: initialValues?.salesPrice !== undefined ? String(initialValues.salesPrice) : '',  // AI CONTEXT: sales-pricing - Initialize sales price field
    entries: initialValues?.entries !== undefined ? String(initialValues.entries) : '',
    credits: initialValues?.credits !== undefined ? String(initialValues.credits) : (initialValues?.entries !== undefined ? String(initialValues.entries) : '1'),
    tier: initialValues?.tier || 'Bronze',
      isSubscription: Boolean(initialValues?.isSubscription) || false,
      interval: initialValues?.interval || 'month',
    adminOnly: initialValues?.adminOnly || false,
    image: initialValues?.image || '/files/Logo.png',
    images: Array.isArray(initialValues?.images) && initialValues.images.length > 0
      ? initialValues.images
      : (initialValues?.image ? [initialValues.image] : []),
    description: initialValues?.description || '',
    category: initialValues?.category || (initialValues?.tier || 'Bronze'),
    inStock: initialValues?.inStock ?? true,
    rating: initialValues?.rating !== undefined ? String(initialValues.rating) : '',
    reviews: initialValues?.reviews !== undefined ? String(initialValues.reviews) : '',
    displayOrder: initialValues?.displayOrder !== undefined ? String(initialValues.displayOrder) : '',
    shippingCost: initialValues?.shippingCost !== undefined ? String(initialValues.shippingCost) : '5'
  }));

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleImageFile = async (file) => {
    if (!file) return;
    try {
      const ref = await uploadProductImage(file);
      setForm(prev => {
        const nextImages = Array.isArray(prev.images) ? [...prev.images, ref] : [ref];
        return { ...prev, image: prev.image || ref, images: nextImages };
      });
    } catch (e) {
      console.error('Image upload failed:', e);
      alert('Image upload failed. Ensure S3 is configured with REACT_APP_S3_BUCKET.');
    }
  };

  const handleMultipleImageFiles = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const uploads = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f) uploads.push(uploadProductImage(f));
      }
      const refs = await Promise.all(uploads);
      setForm(prev => {
        const merged = Array.isArray(prev.images) ? [...prev.images, ...refs] : [...refs];
        return { ...prev, images: merged, image: prev.image || merged[0] || prev.image };
      });
    } catch (e) {
      console.error('Multiple image upload failed:', e);
      alert('Some image uploads failed. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-theme-silver mb-2">Name</label>
        <input className="glass-input" value={form.name} onChange={e => handleChange('name', e.target.value)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Price ($)</label>
          <input type="number" className="glass-input" value={form.price} onChange={e => handleChange('price', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Sales Price ($)</label>
          <input 
            type="number" 
            step="0.01"
            className="glass-input" 
            value={form.salesPrice || ''} 
            onChange={e => handleChange('salesPrice', e.target.value === '' ? null : e.target.value)}
            placeholder="e.g., 17.50"
          />
          <p className="text-xs text-theme-silver-light mt-1">
            Enter dollar amount (not percentage). Example: For 50% off $35, enter <strong>17.50</strong>. Leave empty for no discount.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Shipping cost ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="glass-input"
            value={form.shippingCost}
            onChange={e => handleChange('shippingCost', e.target.value)}
          />
          <p className="text-xs text-theme-silver-light mt-1">
            Default $5. Set 0 for no shipping, or any amount for custom shipping.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Entries</label>
          <input type="number" className="glass-input" value={form.entries} onChange={e => handleChange('entries', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Credits</label>
          <input
            type="number"
            min="1"
            className="glass-input"
            value={form.credits}
            onChange={e => handleChange('credits', e.target.value)}
          />
          <p className="text-xs text-theme-silver-light mt-1">
            Credits awarded per item purchase (default 1).
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Display Order</label>
          <input 
            type="number" 
            className="glass-input" 
            value={form.displayOrder} 
            onChange={e => handleChange('displayOrder', e.target.value)}
            placeholder="1, 2, 3..."
          />
          <p className="text-xs text-theme-silver-light mt-1">
            Lower numbers appear first on the store page. Leave empty for random placement.
          </p>
          {form.displayOrder === '' && (
            <div className="mt-2">
              <p className="text-xs text-theme-silver-light mb-1">Available numbers:</p>
              <div className="flex flex-wrap gap-1">
                {getAvailableDisplayOrders(initialValues?.id).slice(0, 8).map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => handleChange('displayOrder', String(num))}
                    className="text-xs px-2 py-1 bg-theme-silver/10 hover:bg-theme-silver/20 rounded border border-theme-silver/20 text-theme-silver transition-colors"
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Tier</label>
          <select className="glass-input" value={form.tier} onChange={e => handleChange('tier', e.target.value)}>
            <option value="Bronze">Bronze</option>
            <option value="Silver">Silver</option>
            <option value="Gold">Gold</option>
            <option value="Platinum">Platinum</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Product Type</label>
          <select className="glass-input" value={form.isSubscription ? 'yes' : 'no'} onChange={e => handleChange('isSubscription', e.target.value === 'yes')}>
            <option value="no">One-time</option>
            <option value="yes">Subscription</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Admin-only Test Product</label>
          <select
            className="glass-input"
            value={form.adminOnly ? 'yes' : 'no'}
            onChange={e => handleChange('adminOnly', e.target.value === 'yes')}
          >
            <option value="no">No (visible to all customers)</option>
            <option value="yes">Yes (visible only to admins)</option>
          </select>
          <p className="text-xs text-theme-silver-light mt-1">
            Use this for low-price live Stripe test products that only admin accounts can see and purchase.
          </p>
        </div>
      </div>
      {form.isSubscription && (
        <div className="grid grid-cols-2 gap-4">
          <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Billing Interval</label>
            <select className="glass-input" value={form.interval} onChange={e => handleChange('interval', e.target.value)}>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-silver mb-2">In Stock</label>
            <select className="glass-input" value={form.inStock ? 'yes' : 'no'} onChange={e => handleChange('inStock', e.target.value === 'yes')}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2">
        <label className="block text-sm font-medium text-theme-silver">Product Images</label>
        <div className="flex items-center gap-3">
          <input
            id={productInputId}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              await handleImageFile(file);
              e.target.value = '';
            }}
          />
          <label htmlFor={productInputId} className="btn-secondary cursor-pointer">
            Choose Image
          </label>
          <input
            id={productMultiInputId}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={async (e) => {
              const files = e.target.files;
              await handleMultipleImageFiles(files);
              e.target.value = '';
            }}
          />
          <label htmlFor={productMultiInputId} className="btn-secondary cursor-pointer">
            Choose Multiple
          </label>
        </div>
        {Array.isArray(form.images) && form.images.length > 0 ? (
          <div className="mt-2">
            <div className="flex flex-wrap gap-2">
              {form.images.map((img, idx) => (
                <div key={img + idx} className="relative">
                  <S3Image
                    s3KeyOrUrl={img}
                    alt={`Product image ${idx + 1}`}
                    className="w-20 h-20 object-contain glass-card p-1"
                    fallback="/files/Logo.png"
                  />
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      className="btn-secondary text-xs px-2 py-1"
                      onClick={() => setForm(prev => ({ ...prev, image: img }))}
                    >
                      Set Primary
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs px-2 py-1"
                      onClick={() => setForm(prev => {
                        const next = (prev.images || []).filter((x, i) => i !== idx);
                        const nextPrimary = prev.image === img ? (next[0] || '/files/Logo.png') : prev.image;
                        return { ...prev, images: next, image: nextPrimary };
                      })}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <p className="text-xs text-theme-silver-light">
          {isStorageConfigured() ? 'Uploads use Amplify S3 (public) and auto-fill the image reference.' : 'Set REACT_APP_S3_BUCKET and Cognito to enable S3 uploads.'}
        </p>
        <label className="block text-sm font-medium text-theme-silver">Or Image URL / Reference</label>
        <input className="glass-input" value={form.image} onChange={e => handleChange('image', e.target.value)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-theme-silver mb-2">Description</label>
        <textarea className="glass-input" rows="3" value={form.description} onChange={e => handleChange('description', e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Rating</label>
          <input type="number" step="0.1" className="glass-input" value={form.rating} onChange={e => handleChange('rating', e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-theme-silver mb-2">Reviews</label>
          <input type="number" className="glass-input" value={form.reviews} onChange={e => handleChange('reviews', e.target.value)} />
        </div>
      </div>
      <div className="flex space-x-3 mt-4">
        <button onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
        <button onClick={() => {
          const toNumber = (v, parser) => (v === '' || v === null ? 0 : parser(v));
          const toNumberOrNull = (v, parser) => (v === '' || v === null ? null : parser(v));
          const toPositiveIntOrDefault = (v, fallback) => {
            const parsed = parseInt(v, 10);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
          };
          const imagesArray = Array.isArray(form.images) && form.images.length > 0
            ? form.images
            : (form.image ? [form.image] : []);
          onSave({
            ...form,
            image: form.image || imagesArray[0] || '/files/Logo.png',
            images: imagesArray,
            price: toNumber(form.price, Number),
            entries: toNumber(form.entries, parseInt),
            credits: toPositiveIntOrDefault(form.credits, 1),
            isSubscription: Boolean(form.isSubscription),
            interval: form.interval,
            rating: toNumber(form.rating, parseFloat),
            reviews: toNumber(form.reviews, parseInt),
            displayOrder: toNumberOrNull(form.displayOrder, parseInt),
            shippingCost: (() => { const raw = form.shippingCost; const v = toNumber(raw, parseFloat); return (raw === '' || raw === null || Number.isNaN(v) ? 5 : Math.max(0, v)); })(),
          });
        }} className="btn-primary flex-1">Save</button>
      </div>
    </div>
  );
};

// AI CONTEXT: Credit Audit Tab Component
// Provides comprehensive credit audit report generation from Stripe CSV exports
const CreditAuditTab = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [htmlReport, setHtmlReport] = useState(null);
  const csvInputId = useId();

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setCsvFile(file);
      toast.success(`CSV file selected: ${file.name}`);
    }
  };

  const generateComprehensiveAudit = async () => {
    if (!csvFile) {
      toast.error('Please upload a CSV file first');
      return;
    }

    setCsvLoading(true);
    setHtmlReport(null);

    try {
      // Read CSV file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvContent = e.target.result;
        const base64Csv = btoa(csvContent);

        try {
          // Call Lambda endpoint - returns job ID immediately
          const result = await authorizedFetch('/admin/credits/comprehensive-audit', {
            method: 'POST',
            body: JSON.stringify({
              csvFile: base64Csv,
              fileName: csvFile.name
            })
          });

          console.log('🔍 Comprehensive audit response:', result);
          console.log('🔍 result.jobId:', result?.jobId);
          console.log('🔍 result.htmlReport:', result?.htmlReport);

          if (result?.jobId) {
            toast.success('Audit job started. Processing...');
            // Poll for job completion
            pollJobStatus(result.jobId);
          } else if (result?.htmlReport) {
            // Legacy synchronous response (shouldn't happen with new async implementation)
            setHtmlReport(result.htmlReport);
            toast.success('Comprehensive credit audit report generated successfully');
            setCsvLoading(false);
          } else {
            console.error('🔍 No jobId or htmlReport in response:', result);
            toast.error('Failed to start audit job - no job ID received');
            setCsvLoading(false);
          }
        } catch (error) {
          console.error('Error starting comprehensive audit:', error);
          toast.error(error.message || 'Failed to start comprehensive audit');
          setCsvLoading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read CSV file');
        setCsvLoading(false);
      };
      reader.readAsText(csvFile);
    } catch (error) {
      console.error('Error generating comprehensive audit:', error);
      toast.error(error.message || 'Failed to generate comprehensive audit');
      setCsvLoading(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 120; // 2 minutes max (120 * 1 second)
    let attempts = 0;

    const poll = async () => {
      try {
        const result = await authorizedFetch(`/admin/credits/comprehensive-audit/${jobId}`, {
          method: 'GET'
        });

        if (result.status === 'completed') {
          setHtmlReport(result.htmlReport);
          toast.success('Comprehensive credit audit report generated successfully');
          setCsvLoading(false);
        } else if (result.status === 'failed') {
          toast.error(result.error || 'Failed to generate report');
          setCsvLoading(false);
        } else if (result.status === 'processing') {
          attempts++;
          if (attempts >= maxAttempts) {
            toast.error('Report generation timed out. Please try again.');
            setCsvLoading(false);
          } else {
            // Poll again after 1 second
            setTimeout(poll, 1000);
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        attempts++;
        if (attempts >= maxAttempts) {
          toast.error('Failed to check job status. Please refresh and try again.');
          setCsvLoading(false);
        } else {
          setTimeout(poll, 1000);
        }
      }
    };

    // Start polling
    poll();
  };

  const downloadHtmlReport = () => {
    if (!htmlReport) return;
    
    const blob = new Blob([htmlReport], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comprehensive_credit_audit_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-theme-silver">Credit Audit</h2>
      </div>

      {/* Comprehensive Credit Audit Report */}
      <div className="glass-card-dark p-6">
        <h3 className="text-lg font-semibold text-theme-silver mb-4">Comprehensive Credit Audit Report</h3>
        <p className="text-sm text-theme-silver-light mb-4">
          Upload a Stripe payments CSV export to generate a comprehensive credit audit report for all subscribers.
        </p>
        <div className="space-y-4">
          <div>
            <label htmlFor={csvInputId} className="block text-sm text-theme-silver-light mb-2">
              Upload Stripe Payments CSV
            </label>
            <div className="flex gap-4">
              <input
                id={csvInputId}
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="hidden"
              />
              <label
                htmlFor={csvInputId}
                className="flex-1 px-4 py-2 bg-glass-white/5 border border-theme-silver/20 rounded-lg text-theme-silver cursor-pointer hover:bg-glass-white/10 transition-colors flex items-center justify-center"
              >
                {csvFile ? csvFile.name : 'Choose CSV File'}
              </label>
              <button
                onClick={generateComprehensiveAudit}
                disabled={!csvFile || csvLoading}
                className="px-6 py-2 bg-custom-orange text-white rounded-lg hover:bg-custom-orange/80 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {csvLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>

          {htmlReport && (
            <div className="mt-6 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-semibold text-theme-silver">Report Generated</h4>
                <button
                  onClick={downloadHtmlReport}
                  className="px-4 py-2 bg-custom-orange text-white rounded-lg hover:bg-custom-orange/80 transition-colors flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download HTML
                </button>
              </div>
              <div className="border border-theme-silver/20 rounded-lg overflow-hidden">
                <iframe
                  srcDoc={htmlReport}
                  className="w-full h-[600px] bg-white"
                  title="Credit Audit Report"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const giveawayCreateInputId = useId();
  const giveawayEditInputId = useId();
  const giveawayCreateMultiInputId = useId();
  const giveawayEditMultiInputId = useId();
  const { isAdmin } = useAuth();
  const { user } = useAuth();
  const { giveaways, createGiveaway, updateGiveaway, deleteGiveaway, selectWinner, fetchGiveaways } = useGiveaway();
  const { products, createProduct, updateProduct, deleteProduct } = useProducts();
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [giveawayListTab, setGiveawayListTab] = useState('active'); // 'active' | 'past'
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [selectedGiveawayEntries, setSelectedGiveawayEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [reconcileSlotsLoading, setReconcileSlotsLoading] = useState(false);
  const [showWinnerPickerModal, setShowWinnerPickerModal] = useState(false);
  const [revenueData, setRevenueData] = useState({
    total_revenue: 0,
    total_orders: 0,
    average_order_value: 0,
    period: 'all'
  });
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [winners, setWinners] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [referralsData, setReferralsData] = useState({ referrals: [], statistics: {} });
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsSearchTerm, setReferralsSearchTerm] = useState('');
  const [backfillReferralForm, setBackfillReferralForm] = useState({
    referrerEmail: '',
    referredUserEmail: '',
    referralCode: ''
  });
  const [backfillReferralLoading, setBackfillReferralLoading] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  
  // Entry management state
  const [showPrivateEntryModal, setShowPrivateEntryModal] = useState(false);
  const [showUserCreationModal, setShowUserCreationModal] = useState(false);
  const [privateEntryLoading, setPrivateEntryLoading] = useState(false);
  const [userCreationLoading, setUserCreationLoading] = useState(false);
  
  // Support system state
  const [supportTickets, setSupportTickets] = useState([]);
  const [generalInquiries, setGeneralInquiries] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [acquisitionRequests, setAcquisitionRequests] = useState([]);
  const [acquisitionLoading, setAcquisitionLoading] = useState(false);

  // iOS modal scroll containment (mirror of Giveaways page behavior)
  useEffect(() => {
    const anyModalOpen = showCreateForm || showEditForm || showCreateProduct || showEditProduct || showUserProfileModal || showDeleteConfirmModal || showSmsModal;
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    const originalOverscroll = document.body.style.overscrollBehaviorY || document.body.style.overscrollBehavior;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.overscrollBehavior = 'contain';
      document.body.style.overscrollBehaviorY = 'contain';
    } else {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.touchAction = originalTouchAction || '';
      if (originalOverscroll) {
        document.body.style.overscrollBehavior = originalOverscroll;
        document.body.style.overscrollBehaviorY = originalOverscroll;
      } else {
        document.body.style.overscrollBehavior = '';
        document.body.style.overscrollBehaviorY = '';
      }
    }
    return () => {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.touchAction = originalTouchAction || '';
      if (originalOverscroll) {
        document.body.style.overscrollBehavior = originalOverscroll;
        document.body.style.overscrollBehaviorY = originalOverscroll;
      } else {
        document.body.style.overscrollBehavior = '';
        document.body.style.overscrollBehaviorY = '';
      }
    };
  }, [showCreateForm, showEditForm, showCreateProduct, showEditProduct, showUserProfileModal, showDeleteConfirmModal, showSmsModal]);

  const stopScrollPropagation = (event) => { event.stopPropagation(); };

  // AI CONTEXT: business-logic - Real user data from AWS Cognito
  // Reasoning: Display actual user data for admin management
  // Constraints: Must handle loading states and errors
  // Future: Implement real-time user data updates

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  // Controls whether the Users tab shows all users or subscribers only.
  // Default is "all"; clicking the Subscribers stat card will switch this to "subscribers".
  const [userFilterMode, setUserFilterMode] = useState('all'); // 'all' | 'subscribers'

  // AI CONTEXT: data-loading - Load real user data from AWS Cognito
  // Reasoning: Fetch actual user data for admin dashboard
  // Constraints: Must handle loading states and errors
  // Future: Implement real-time updates and pagination

  // Pagination state for users
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  
  // AI CONTEXT: performance-optimization - Client-side user data caching
  // Journal: J-20250117-04
  // Purpose: Cache user data locally to enable instant pagination and reduce API calls
  // Cache Strategy: Store user data in localStorage with TTL, refresh on cache miss
  const USER_CACHE_KEY = 'admin_users_cache_v3';
  const USER_CACHE_TIMESTAMP_KEY = 'admin_users_cache_timestamp_v3';
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  const usersHaveAccountStats = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return false;
    return list.some((user) => {
      const credits = user?.credits;
      const hasCreditObject = credits && typeof credits === 'object' && ('total' in credits || 'bronze' in credits || 'totalCredits' in credits);
      return hasCreditObject || Number(user?.purchases) > 0 || Number(user?.entries) > 0;
    });
  }, []);

  const getCachedUsers = useCallback(() => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      const timestamp = localStorage.getItem(USER_CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        if (cacheAge < CACHE_TTL) {
          const users = JSON.parse(cached);
          
          // Validate cached data structure
          if (Array.isArray(users) && users.length > 0) {
            // Check if first user has expected structure
            const firstUser = users[0];
            if (firstUser && typeof firstUser === 'object' && firstUser.id && firstUser.email && usersHaveAccountStats(users)) {
              console.log(`✅ Using cached users (age: ${Math.round(cacheAge / 1000)}s)`);
              return users;
            } else {
              console.log('❌ Cached data structure invalid, clearing cache...');
              localStorage.removeItem(USER_CACHE_KEY);
              localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
            }
          } else {
            console.log('❌ Cached data is not a valid array, clearing cache...');
            localStorage.removeItem(USER_CACHE_KEY);
            localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
          }
        } else {
          console.log(`⏰ Cache expired (age: ${Math.round(cacheAge / 1000)}s), clearing...`);
          localStorage.removeItem(USER_CACHE_KEY);
          localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
        }
      }
    } catch (error) {
      console.error('Error reading user cache:', error);
      // Clear corrupted cache
      localStorage.removeItem(USER_CACHE_KEY);
      localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
    }
    return null;
  }, [CACHE_TTL, usersHaveAccountStats]);

  const setCachedUsers = useCallback((users) => {
    try {
      // Validate data before caching
      if (Array.isArray(users) && users.length > 0) {
        const firstUser = users[0];
        if (firstUser && typeof firstUser === 'object' && firstUser.id && firstUser.email && usersHaveAccountStats(users)) {
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(users));
          localStorage.setItem(USER_CACHE_TIMESTAMP_KEY, Date.now().toString());
          console.log(`✅ Cached ${users.length} users`);
        } else {
          console.log('❌ Invalid user data structure, not caching');
        }
      } else {
        console.log('❌ Invalid users array, not caching');
      }
    } catch (error) {
      console.error('Error caching users:', error);
    }
  }, [usersHaveAccountStats]);

  const loadUsers = useCallback(async (page = 1) => {
    try {
      setUsersLoading(true);

      if (!isApiConfigured()) {
        const userData = await authService.getAllUsers();
        setUsers(userData);
        setAllUsers(userData);
        setTotalUsers(userData.length);
        setHasMoreUsers(userData.length > usersPerPage);
        setCurrentUserPage(1);
        return;
      }

      // Load the full admin user list once, then paginate in the table.
      // Page-by-page /admin/users fetches were returning later pages without credits.
      let list = [];
      try {
        const searchResponse = await authorizedFetch('/admin/users/search?q=&limit=500', { method: 'GET' });
        if (searchResponse && Array.isArray(searchResponse.users)) {
          list = searchResponse.users;
        }
      } catch (searchError) {
        console.warn('Search endpoint failed, falling back to users list:', searchError);
      }

      if (!usersHaveAccountStats(list)) {
        const response = await authorizedFetch('/admin/users?limit=500&offset=0', { method: 'GET' });
        if (response && Array.isArray(response.users)) {
          list = response.users;
          if (response.pagination?.total) {
            setTotalUsers(response.pagination.total);
          }
        }
      }

      if (!usersHaveAccountStats(list)) {
        throw new Error('User stats were missing from the admin users API');
      }

      setAllUsers(list);
      setUsers(list);
      setCachedUsers(list);
      setTotalUsers(list.length);
      const maxPage = Math.ceil(list.length / usersPerPage) || 1;
      const nextPage = Math.min(Math.max(page, 1), maxPage);
      setCurrentUserPage(nextPage);
      setHasMoreUsers(nextPage < maxPage);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load user credits and entries. Please refresh.');
    } finally {
      setUsersLoading(false);
    }
  }, [usersPerPage, usersHaveAccountStats, setCachedUsers]);

  const loadNextPage = () => {
    if (usersLoading) return;
    const maxPage = Math.ceil(filteredUsers.length / usersPerPage) || 1;
    if (currentUserPage < maxPage) {
      setCurrentUserPage((prev) => Math.min(prev + 1, maxPage));
    }
  };

  const loadPreviousPage = () => {
    if (currentUserPage <= 1 || usersLoading) return;
    setCurrentUserPage((prev) => Math.max(1, prev - 1));
  };

  // AI CONTEXT: admin-search - Load all users for comprehensive search
  // Reasoning: Search should work across all users, not just paginated results
  // Constraints: Must handle loading states and API failures gracefully
  // Future: Consider implementing server-side search for better performance

  const loadAllUsersForSearch = useCallback(async (bypassCache = false) => {
    try {
      setSearchLoading(true);
      
      // Check cache first for instant search (unless bypassing)
      if (!bypassCache) {
        const cachedUsers = getCachedUsers();
        if (cachedUsers && cachedUsers.length > 0) {
          setAllUsers(cachedUsers);
          setSearchLoading(false);
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Using ${cachedUsers.length} cached users for search (instant)`);
          }
          return cachedUsers;
        }
      } else if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Bypassing cache to fetch fresh user data...');
      }
      
      // Cache miss - try optimized search endpoint first
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Cache miss, loading all users for search...');
      }
      
      // Try the new optimized search endpoint for faster loading
      // Use a large limit to get all users for comprehensive search
      try {
        const searchResponse = await authorizedFetch('/admin/users/search?q=&limit=500', { method: 'GET' });
        if (searchResponse && Array.isArray(searchResponse.users)) {
          setAllUsers(searchResponse.users);
          setCachedUsers(searchResponse.users);
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Loaded ${searchResponse.users.length} users via search endpoint (cached: ${searchResponse.cached})`);
          }
          return searchResponse.users;
        }
      } catch (searchError) {
        console.warn('Search endpoint failed, falling back to getAllUsers:', searchError);
      }
      
      // Fallback to original method
      const allUsersData = await authService.getAllUsers();
      setAllUsers(allUsersData);
      
      // Cache the results for future searches
      if (allUsersData && allUsersData.length > 0) {
        setCachedUsers(allUsersData);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Loaded ${allUsersData.length} users for search`);
      }
      
      return allUsersData;
    } catch (error) {
      console.error('Error loading all users for search:', error);
      toast.error('Failed to load all users for search');
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [getCachedUsers, setCachedUsers]);

  // AI CONTEXT: performance-optimization - Cache refresh function
  // Journal: J-20250117-04
  // Purpose: Allow manual cache refresh for fresh data
  const refreshUserCache = useCallback(async () => {
    try {
      // Clear existing cache
      localStorage.removeItem(USER_CACHE_KEY);
      localStorage.removeItem(USER_CACHE_TIMESTAMP_KEY);
      
      // Force reload from API
      await loadUsers(1, false);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ User cache refreshed');
      }
    } catch (error) {
      console.error('Error refreshing user cache:', error);
    }
  }, [loadUsers]);

  // Helper: apply subscribers-only filter when requested
  const applyUserFilterMode = useCallback((list) => {
    if (!Array.isArray(list)) return [];

    if (userFilterMode === 'subscribers') {
      return list.filter(user =>
        user?.isSubscriber === true ||
        (user?.subscriptionTier && user.subscriptionTier !== 'Free')
      );
    }

    return list;
  }, [userFilterMode]);

  // Update filtered users when users data, search term, or filter mode changes
  useEffect(() => {
    const performSearch = async () => {
      // No search term – just apply the current filter mode
      if (!userSearchTerm.trim()) {
        const baseList = allUsers.length > 0 ? allUsers : users;
        if (userFilterMode === 'subscribers' && allUsers.length === 0) {
          await loadAllUsersForSearch();
        }
        setFilteredUsers(applyUserFilterMode(baseList));
        return;
      }

      // With a search term, search across all users (loading them if needed)
      if (allUsers.length === 0) {
        await loadAllUsersForSearch();
      }

      const term = userSearchTerm.toLowerCase().trim();
      const searchSource = allUsers.length > 0 ? allUsers : users;
      
      const filtered = searchSource.filter(user => {
        // Search in name (first name and last name)
        const name = (user.name || '').toLowerCase();
        const nameMatch = name.includes(term);
        
        // Search in email
        const email = (user.email || '').toLowerCase();
        const emailMatch = email.includes(term);
        
        // Search in phone number
        const phone = (user.phone || '').toLowerCase();
        const phoneMatch = phone.includes(term);
        
        // Search in username (if available)
        const username = (user.username || user.id || '').toLowerCase();
        const usernameMatch = username.includes(term);
        
        // Search in first name and last name separately (if available)
        const firstName = (user.firstName || user.given_name || '').toLowerCase();
        const lastName = (user.lastName || user.family_name || '').toLowerCase();
        const firstNameMatch = firstName.includes(term);
        const lastNameMatch = lastName.includes(term);
        
        // Split name into first and last name for more specific searches
        const nameParts = name.split(' ');
        const namePartsMatch = nameParts.some(part => part.includes(term));
        
        return nameMatch || emailMatch || phoneMatch || usernameMatch || 
               firstNameMatch || lastNameMatch || namePartsMatch;
      });
      
      setFilteredUsers(applyUserFilterMode(filtered));
    };

    // Optimized debounce - faster for cached data, slower for API calls
    const debounceTime = allUsers.length > 0 ? 100 : 300; // Faster for cached data
    const timeoutId = setTimeout(() => {
      performSearch();
    }, debounceTime);

    return () => clearTimeout(timeoutId);
  }, [users, userSearchTerm, allUsers, loadAllUsersForSearch, applyUserFilterMode, userFilterMode]);

  useEffect(() => {
    const maxPage = Math.ceil(filteredUsers.length / usersPerPage) || 1;
    if (currentUserPage > maxPage) {
      setCurrentUserPage(maxPage);
    }
  }, [filteredUsers.length, usersPerPage, currentUserPage]);

  const loadUserProfile = async (email) => {
    try {
      setProfileLoading(true);

      const normalized = String(email || '').trim().toLowerCase();
      // Phone/name live on Cognito-backed rows; `users` is only the current page — search uses allUsers / filteredUsers
      const userFromList =
        users.find((u) => String(u.email || '').toLowerCase() === normalized) ||
        filteredUsers.find((u) => String(u.email || '').toLowerCase() === normalized) ||
        allUsers.find((u) => String(u.email || '').toLowerCase() === normalized);

      const profileData = await getUserProfile(email);

      const combinedProfile = {
        ...profileData,
        ...userFromList,
        email: email || profileData?.email,
        phone:
          userFromList?.phone ||
          profileData?.phone ||
          userFromList?.phone_number ||
          profileData?.phone_number ||
          '',
      };

      setSelectedUserProfile(combinedProfile);
      setShowUserProfileModal(true);
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Auto-refresh users when users tab is selected
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab, loadUsers]);

  // Load all users when overview tab is selected to ensure accurate subscriber count
  // Force refresh to get latest subscription data
  useEffect(() => {
    if (activeTab === 'overview') {
      // Always fetch fresh data for accurate subscriber count (bypass cache)
      loadAllUsersForSearch(true).then((loadedUsers) => {
        // Update totalUsers immediately to prevent showing paginated count
        if (loadedUsers && loadedUsers.length > 0) {
          setTotalUsers(loadedUsers.length);
        }
      });
    }
  }, [activeTab, loadAllUsersForSearch]);

  // AI CONTEXT: revenue-tracking - Load real revenue data from Stripe
  // Reasoning: Admin needs to see actual revenue data for business insights
  // Constraints: Must handle loading states and API failures
  // Future: Add more detailed revenue analytics and charts

  const loadRevenueData = async (period = 'all') => {
    try {
      setRevenueLoading(true);
      if (isApiConfigured()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Admin: Calling revenue API...');
        }
        const response = await authorizedFetch(`/admin/revenue?period=${period}`, { method: 'GET' });
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Admin: Revenue API response:', response);
        }
        if (response && typeof response.total_revenue === 'number') {
          setRevenueData(response);
        } else {
          console.warn('Invalid revenue response:', response);
          // Fallback to zero revenue
          setRevenueData({
            total_revenue: 0,
            total_orders: 0,
            average_order_value: 0,
            period: period
          });
        }
      } else {
        // Fallback for development
        setRevenueData({
          total_revenue: 2450, // Mock data
          total_orders: 12,
          average_order_value: 204.17,
          period: period
        });
      }
    } catch (error) {
      console.error('Error loading revenue data:', error);
      toast.error('Failed to load revenue data');
      // Fallback to zero revenue
      setRevenueData({
        total_revenue: 0,
        total_orders: 0,
        average_order_value: 0,
        period: period
      });
    } finally {
      setRevenueLoading(false);
    }
  };

  // Load revenue data on component mount and when overview tab is selected
  useEffect(() => {
    if (activeTab === 'overview') {
      loadRevenueData();
      loadRecentActivity();
    }
  }, [activeTab]);

  // Load winners when winners tab is selected
  useEffect(() => {
    if (activeTab === 'winners') {
      loadWinners();
    }
  }, [activeTab]);

  // AI CONTEXT: admin-referrals - Load all referrals data for admin oversight
  // Reasoning: Admin needs comprehensive view of referral system with audit capabilities
  // Constraints: Must handle loading states and API failures
  // Future: Add date range filtering and export functionality
  
  const loadReferralsData = async () => {
    try {
      setReferralsLoading(true);
      if (isApiConfigured()) {
        const response = await getAdminAllReferrals();
        if (response && response.referrals) {
          setReferralsData(response);
        } else {
          console.warn('Invalid referrals response:', response);
          setReferralsData({ referrals: [], statistics: {} });
        }
      } else {
        // Fallback for development
        setReferralsData({
          referrals: [],
          statistics: {
            totalReferrals: 0,
            creditsIssued: 0,
            creditsPending: 0,
            uniqueReferrers: 0,
            topReferrers: []
          }
        });
      }
    } catch (error) {
      console.error('Error loading referrals data:', error);
      toast.error('Failed to load referrals data');
      setReferralsData({ referrals: [], statistics: {} });
    } finally {
      setReferralsLoading(false);
    }
  };

  const handleBackfillReferral = async (e) => {
    e.preventDefault();
    if (!isApiConfigured()) {
      toast.error('API not configured');
      return;
    }

    const payload = {
      referrerEmail: backfillReferralForm.referrerEmail.trim() || undefined,
      referredUserEmail: backfillReferralForm.referredUserEmail.trim() || undefined,
      referralCode: backfillReferralForm.referralCode.trim().toUpperCase() || undefined
    };

    if (!payload.referredUserEmail) {
      toast.error('Referred user email is required');
      return;
    }

    if (!payload.referralCode && !payload.referrerEmail) {
      toast.error('Provide a referrer email or referral code');
      return;
    }

    try {
      setBackfillReferralLoading(true);
      const response = await backfillReferral(payload);
      if (response && response.success) {
        toast.success(response.message || 'Referral backfilled. The referrer and referred customer will see it in their dashboards (refresh if needed).');
      } else {
        toast.success(response?.message || 'Referral backfilled');
      }
      setBackfillReferralForm({ referrerEmail: '', referredUserEmail: '', referralCode: '' });
      loadReferralsData();
    } catch (error) {
      console.error('Error backfilling referral:', error);
      toast.error(error.message || 'Failed to backfill referral');
    } finally {
      setBackfillReferralLoading(false);
    }
  };

  // Load referrals when referrals tab is selected
  useEffect(() => {
    if (activeTab === 'referrals') {
      loadReferralsData();
    }
  }, [activeTab]);

  const loadSupportTickets = async () => {
    try {
      console.log('📋 loadSupportTickets called, API configured:', isApiConfigured());
      setSupportLoading(true);
      if (isApiConfigured()) {
        console.log('🌐 Making API call to /admin/support/tickets');
        const response = await authorizedFetch('/admin/support/tickets', { method: 'GET' });
        console.log('📋 Support tickets API response:', response);
        if (response && response.success && Array.isArray(response.tickets)) {
          console.log('✅ Setting', response.tickets.length, 'support tickets');
          setSupportTickets(response.tickets);
        } else {
          console.warn('Invalid support tickets response:', response);
          setSupportTickets([]);
        }
      } else {
        // Fallback for development
        setSupportTickets([
          {
            id: 'demo-ticket-1',
            subject: 'Website not loading',
            message: 'I cannot access the website on my mobile device',
            priority: 'high',
            status: 'open',
            userEmail: 'user@example.com',
            userName: 'John Doe',
            userPhone: '+1234567890',
            createdAt: Date.now() - 3600000, // 1 hour ago
            updatedAt: Date.now() - 3600000,
            assignedTo: null,
            notes: []
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading support tickets:', error);
      toast.error('Failed to load support tickets');
      setSupportTickets([]);
    } finally {
      setSupportLoading(false);
    }
  };

  const loadGeneralInquiries = async () => {
    try {
      console.log('📋 loadGeneralInquiries called, API configured:', isApiConfigured());
      setInquiriesLoading(true);
      if (isApiConfigured()) {
        console.log('🌐 Making API call to /admin/support/inquiries');
        const response = await authorizedFetch('/admin/support/inquiries', { method: 'GET' });
        console.log('📋 General inquiries API response:', response);
        if (response && response.success && Array.isArray(response.inquiries)) {
          console.log('✅ Setting', response.inquiries.length, 'general inquiries');
          setGeneralInquiries(response.inquiries);
        } else {
          console.warn('Invalid general inquiries response:', response);
          setGeneralInquiries([]);
        }
      } else {
        // Fallback for development
        setGeneralInquiries([
          {
            id: 'demo-inquiry-1',
            subject: 'General question about the platform',
            message: 'I have a question about how the giveaway system works',
            status: 'new',
            userEmail: 'user@example.com',
            userName: 'Jane Smith',
            userPhone: '+1987654321',
            createdAt: Date.now() - 7200000, // 2 hours ago
            updatedAt: Date.now() - 7200000,
            assignedTo: null,
            notes: []
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading general inquiries:', error);
      toast.error('Failed to load general inquiries');
      setGeneralInquiries([]);
    } finally {
      setInquiriesLoading(false);
    }
  };

  // Load support tickets when support tab is selected
  useEffect(() => {
    if (activeTab === 'support') {
      console.log('🔄 Loading support tickets for activeTab:', activeTab);
      loadSupportTickets();
    }
  }, [activeTab]);

  // Load general inquiries when inquiries tab is selected
  useEffect(() => {
    if (activeTab === 'inquiries') {
      console.log('🔄 Loading general inquiries for activeTab:', activeTab);
      loadGeneralInquiries();
    }
  }, [activeTab]);

  const loadAcquisitionRequests = async () => {
    try {
      setAcquisitionLoading(true);
      if (isApiConfigured()) {
        const response = await getAdminAcquisitionRequests();
        if (response && response.success && Array.isArray(response.requests)) {
          setAcquisitionRequests(response.requests);
        } else {
          setAcquisitionRequests([]);
        }
      } else {
        setAcquisitionRequests([]);
      }
    } catch (error) {
      console.error('Error loading acquisition requests:', error);
      toast.error('Failed to load acquisition requests');
      setAcquisitionRequests([]);
    } finally {
      setAcquisitionLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'acquisition') {
      loadAcquisitionRequests();
    }
  }, [activeTab]);

  // AI CONTEXT: activity-tracking - Load real activity data from DynamoDB
  // Reasoning: Admin needs to see actual recent activity for monitoring
  // Constraints: Must handle loading states and API failures
  // Future: Add more activity types and filtering options

  const loadRecentActivity = async () => {
    try {
      setActivityLoading(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Debug: isApiConfigured():', isApiConfigured());
      }
      if (isApiConfigured()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Debug: Loading recent activity from API...');
        }
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Admin: Calling activity API...');
          }
          const response = await authorizedFetch('/admin/activity?limit=10&_t=' + Date.now(), { method: 'GET' });
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Admin: Activity API response:', response);
          }
          if (response && Array.isArray(response.activities)) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔍 Debug: Found activities:', response.activities.length);
            }
            setRecentActivity(response.activities);
          } else {
            console.warn('Invalid activity response:', response);
            setRecentActivity([]);
          }
        } catch (apiError) {
          console.error('Admin activity API call failed:', apiError);
          if (process.env.NODE_ENV === 'development') {
            console.error('🔍 Debug: API error details:', {
              message: apiError.message,
              name: apiError.name,
              stack: apiError.stack
            });
          }
          setRecentActivity([]);
        }
      } else {
        // Fallback for development
        setRecentActivity([
          {
            type: 'purchase',
            title: 'Customer purchase completed',
            description: 'John Smith (john@example.com) purchased $89.99 worth of entries (5 entries)',
            timestamp: Date.now() - 3600000, // 1 hour ago
            email: 'john@example.com',
            customer_name: 'John Smith',
            amount: 89.99,
            entries: 5
          },
          {
            type: 'giveaway_entry',
            title: 'User entered giveaway',
            description: 'Jane Doe (jane@example.com) entered slot 5 in Tactical Gear Bundle',
            timestamp: Date.now() - 7200000, // 2 hours ago
            email: 'jane@example.com',
            customer_name: 'Jane Doe',
            giveaway_title: 'Tactical Gear Bundle'
          },
          {
            type: 'purchase',
            title: 'Customer purchase completed',
            description: 'Mike Johnson (mike@example.com) purchased $149.99 worth of entries (10 entries)',
            timestamp: Date.now() - 10800000, // 3 hours ago
            email: 'mike@example.com',
            customer_name: 'Mike Johnson',
            amount: 149.99,
            entries: 10
          },
          {
            type: 'user_registration',
            title: 'New user registered',
            description: 'sarah@example.com joined the platform',
            timestamp: Date.now() - 14400000, // 4 hours ago
            email: 'sarah@example.com'
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
      toast.error('Failed to load recent activity');
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // AI CONTEXT: winner-tracking - Load all winners from DynamoDB
  // Reasoning: Admin needs to see all automatically selected winners
  // Constraints: Must handle loading states and API failures
  // Future: Add winner notification system

  const loadWinners = async () => {
    try {
      setWinnersLoading(true);
      if (isApiConfigured()) {
        const response = await authorizedFetch('/admin/winners', { method: 'GET' });
        if (response && Array.isArray(response.giveaways)) {
          setWinners(response.giveaways);
        } else {
          console.warn('Invalid winners response:', response);
          setWinners([]);
        }
      } else {
        // Fallback for development
        setWinners([
          {
            giveawayId: 'demo-1',
            giveaway: {
              title: 'Tactical Gear Bundle',
              tier: 'Gold',
              value: 500,
              endDate: '2024-01-15'
            },
            winners: [
              {
                id: 'winner-1',
                email: 'winner@example.com',
                slotNumber: 42,
                date: '2024-01-10',
                tier: 'Gold',
                selectedAt: '2024-01-16T02:00:00Z',
                method: 'automatic'
              }
            ],
            totalWinners: 1
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading winners:', error);
      toast.error('Failed to load winners');
      setWinners([]);
    } finally {
      setWinnersLoading(false);
    }
  };

  const [newGiveaway, setNewGiveaway] = useState({
    title: '',
    description: '',
    tier: 'Bronze',
    value: 0,
    totalSlots: 100,
    entryLimit: 5, // AI CONTEXT: business-logic - Default entry limit per user per giveaway
    startDate: '',
    endDate: '',
    earlyAccessDate: '',
    image: '/files/Logo.png',
    images: [],
    sendPremiumEmails: false,
    sendFreeUserEmails: false,
    sendPremiumSMS: false,
    sendFreeUserSMS: false,
    subscriptionVisibilityOnly: false
  });

  // AI CONTEXT: ux - Format date display
  // Reasoning: Provide consistent date formatting
  // Constraints: Must handle various date formats
  // Future: Consider timezone support

  // AI CONTEXT: ux - Format relative time for activity
  // Reasoning: Show how long ago activities occurred
  // Constraints: Must handle Unix timestamps
  // Future: Add more granular time formatting

  const formatRelativeTime = (timestamp) => {
    const now = Date.now();
    const diff = now - (timestamp * 1000); // Convert Unix timestamp to milliseconds
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    
    return formatDate(timestamp * 1000, { includeTime: false });
  };

  // AI CONTEXT: security - Admin access control
  // Reasoning: Ensure only admin users can access
  // Constraints: Must check admin privileges
  // Future: Implement role-based access control

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-theme-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-theme-silver mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-silver mb-2">Access Denied</h2>
          <p className="text-theme-silver-light">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  // AI CONTEXT: business-logic - Handle giveaway creation
  // Reasoning: Allow admins to create new giveaways
  // Constraints: Must validate required fields
  // Future: Add advanced giveaway configuration options

  const handleCreateGiveaway = async () => {
    if (!newGiveaway.title || !newGiveaway.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    const imagesArray = Array.isArray(newGiveaway.images) && newGiveaway.images.length > 0
      ? newGiveaway.images
      : (newGiveaway.image ? [newGiveaway.image] : []);
    const created = await createGiveaway({
      ...newGiveaway,
      image: newGiveaway.image || imagesArray[0] || '/files/Logo.png',
      images: imagesArray,
      availableSlots: newGiveaway.totalSlots,
      category: newGiveaway.tier,
      isActive: true,
      status: 'active'
    });

    if (!created) return;

    setNewGiveaway({
      title: '',
      description: '',
      tier: 'Bronze',
      value: 0,
      totalSlots: 100,
      entryLimit: 5, // AI CONTEXT: business-logic - Reset to default entry limit
      startDate: '',
      endDate: '',
      earlyAccessDate: '',
      image: '/files/Logo.png',
      images: [],
      sendPremiumEmails: false,
      sendFreeUserEmails: false,
      sendPremiumSMS: false,
      sendFreeUserSMS: false,
      subscriptionVisibilityOnly: false
    });
    setShowCreateForm(false);
  };

  // AI CONTEXT: ux - Handle giveaway editing
  // Reasoning: Allow admins to modify existing giveaways
  // Constraints: Must preserve existing data
  // Future: Add version control for changes

  const handleEditGiveaway = (giveaway) => {
    // AI CONTEXT: business-logic - Ensure entryLimit has a default value for backward compatibility
    const giveawayWithDefaults = {
      ...giveaway,
      entryLimit: giveaway.entryLimit ?? 5 // Default to 5 if not set
    };
    setSelectedGiveaway(giveawayWithDefaults);
    setShowEditForm(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowEditProduct(true);
  };

  // AI CONTEXT: business-logic - Handle giveaway deletion
  // Reasoning: Allow admins to remove giveaways
  // Constraints: Must confirm destructive action
  // Future: Add soft delete with recovery options

  const handleDeleteGiveaway = (giveawayId) => {
    if (window.confirm('Are you sure you want to delete this giveaway?')) {
      deleteGiveaway(giveawayId);
    }
  };

  // AI CONTEXT: business-logic - Handle giveaway updates
  // Reasoning: Save modified giveaway data
  // Constraints: Must validate changes
  // Future: Add audit trail for changes

  const handleUpdateGiveaway = () => {
    if (!selectedGiveaway) return;

    updateGiveaway(selectedGiveaway.id, {
      title: selectedGiveaway.title,
      description: selectedGiveaway.description,
      tier: selectedGiveaway.tier,
      value: selectedGiveaway.value,
      totalSlots: selectedGiveaway.totalSlots,
      availableSlots: selectedGiveaway.availableSlots,
      entryLimit: selectedGiveaway.entryLimit || 5, // AI CONTEXT: business-logic - Include entry limit in updates
      startDate: selectedGiveaway.startDate,
      endDate: selectedGiveaway.endDate,
      earlyAccessDate: selectedGiveaway.earlyAccessDate,
      image: selectedGiveaway.image,
      images: Array.isArray(selectedGiveaway.images) ? selectedGiveaway.images : [],
      sendPremiumEmails: selectedGiveaway.sendPremiumEmails,
      sendFreeUserEmails: selectedGiveaway.sendFreeUserEmails,
      sendPremiumSMS: selectedGiveaway.sendPremiumSMS,
      sendFreeUserSMS: selectedGiveaway.sendFreeUserSMS,
      subscriptionVisibilityOnly: selectedGiveaway.subscriptionVisibilityOnly
    });

    setShowEditForm(false);
    setSelectedGiveaway(null);
  };

  const handleReconcileSlots = async () => {
    if (!selectedGiveaway?.id || !isApiConfigured()) {
      toast.error('API not available');
      return;
    }
    try {
      setReconcileSlotsLoading(true);
      const res = await authorizedFetch(`/admin/giveaways/${selectedGiveaway.id}/reconcile-slots`, {
        method: 'POST',
      });
      if (res?.availableSlots !== undefined) {
        setSelectedGiveaway((prev) =>
          prev
            ? {
                ...prev,
                availableSlots: res.availableSlots,
                totalSlots: res.totalSlots ?? prev.totalSlots,
              }
            : null
        );
      }
      await fetchGiveaways();
      toast.success(
        `Slot counts updated: ${res?.availableSlots ?? '?'} available of ${res?.totalSlots ?? '?'} (${res?.takenSlots ?? '?'} filled)`
      );
    } catch (error) {
      console.error('Reconcile slots:', error);
      toast.error(error?.message || 'Failed to reconcile slot counts');
    } finally {
      setReconcileSlotsLoading(false);
    }
  };

  // AI CONTEXT: business-logic - Handle winner selection
  // Reasoning: Allow admins to select giveaway winners
  // Constraints: Must ensure fair selection process
  // Future: Add automated winner selection algorithms

  const handleSelectWinner = (giveawayId) => {
    const winner = selectWinner(giveawayId, 'random');
    if (winner) {
      toast.success(`Winner selected for giveaway!`);
    }
  };

  // AI CONTEXT: admin-functionality - View detailed giveaway entries
  // Reasoning: Allow admins to see all entries with slot numbers for manual winner selection
  // Constraints: Must handle loading states and errors
  // Future: Add manual winner selection from specific slots

  const handleViewEntries = async (giveaway) => {
    try {
      setEntriesLoading(true);
      setShowEntriesModal(true);
      setSelectedGiveaway(giveaway); // Set the selected giveaway
      
      if (isApiConfigured()) {
        const response = await authorizedFetch(`/giveaways/${giveaway.id}/entries`, { method: 'GET' });
        if (response && Array.isArray(response.entries)) {
          setSelectedGiveawayEntries(response.entries);
        } else {
          setSelectedGiveawayEntries([]);
          toast.error('Failed to load entries');
        }
      } else {
        // Fallback to local entries if API not configured
        setSelectedGiveawayEntries(giveaway.entries || []);
      }
    } catch (error) {
      console.error('Error loading entries:', error);
      toast.error('Failed to load giveaway entries');
      setSelectedGiveawayEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleAutomaticWinnerSelection = async () => {
    console.log('🔍 Debug: handleAutomaticWinnerSelection called');
    console.log('🔍 Debug: selectedGiveaway:', selectedGiveaway);
    
    if (!selectedGiveaway) {
      console.log('🔍 Debug: Missing selectedGiveaway');
      toast.error('No giveaway selected');
      return;
    }

    // Confirm the selection
    if (!window.confirm(`Are you sure you want to automatically select a random winner for "${selectedGiveaway.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setEntriesLoading(true);
      
      console.log('🔍 Debug: Sending automatic winner selection request...');
      console.log('🔍 Debug: selectedGiveaway:', selectedGiveaway);
      
      const requestBody = {
        giveawayId: selectedGiveaway.id
      };
      
      console.log('🔍 Debug: Request body:', requestBody);
      
      const response = await authorizedFetch('/admin/select-automatic-winner', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      console.log('🔍 Debug: Response received:', response);

      if (response && response.message) {
        console.log('🔍 Debug: Winner selection successful, response:', response);
        const winner = response.winner;
        toast.success(`Winner selected automatically: ${winner.email} (Slot ${winner.slot})`);
        
        // Close the entries modal
        setShowEntriesModal(false);
        
        // Refresh giveaways to show updated status
        await fetchGiveaways();
        
        // Refresh winners if on winners tab
        if (activeTab === 'winners') {
          await loadWinners();
        }
        
        // Clear selected giveaway
        setSelectedGiveaway(null);
        setSelectedGiveawayEntries([]);
      } else {
        console.log('🔍 Debug: Winner selection failed, response:', response);
        toast.error('Failed to select winner');
      }
    } catch (error) {
      console.error('Error selecting winner:', error);
      toast.error(error.message || 'Failed to select winner');
    } finally {
      setEntriesLoading(false);
    }
  };

  // Debug function to test entries API
  const debugEntriesAPI = async () => {
    try {
      console.log('🔍 Debug: Testing entries API...');
      console.log('🔍 Debug: User isAdmin:', user?.isAdmin);
      console.log('🔍 Debug: Current giveaways:', giveaways);
      
      if (giveaways.length > 0) {
        const firstGiveaway = giveaways[0];
        console.log('🔍 Debug: Testing with giveaway:', firstGiveaway.id);
        
        const response = await authorizedFetch(`/giveaways/${firstGiveaway.id}/entries`, { method: 'GET' });
        console.log('🔍 Debug: Entries API response:', response);
        
        if (response && Array.isArray(response.entries)) {
          console.log('🔍 Debug: Found entries:', response.entries.length);
          toast.success(`Found ${response.entries.length} entries for ${firstGiveaway.title}`);
        } else {
          console.log('🔍 Debug: No entries found or invalid response');
          toast.error('No entries found or invalid response');
        }
      }
    } catch (error) {
      console.error('🔍 Debug: API error:', error);
      toast.error('API error: ' + error.message);
    }
  };

  // AI CONTEXT: business-logic - Export comprehensive user data
  // Reasoning: Allow admins to export ALL users with complete data (address, phone, etc.)
  // Constraints: Must handle data privacy and large datasets
  // Future: Add scheduled data exports and progress tracking

  const exportUserData = async () => {
    try {
      setUsersLoading(true);
      toast.loading('Exporting all user data...', { id: 'export-toast' });
      
      // Call the new comprehensive export endpoint
      const response = await authorizedFetch('/admin/users/export', { method: 'GET' });
      
      if (response && response.csv) {
        // Create and download the CSV file
        const blob = new Blob([response.csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `comprehensive_user_data_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${response.totalUsers} users with complete data`, { id: 'export-toast' });
      } else {
        throw new Error('Invalid response from export endpoint');
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Export failed: ${error.message}`, { id: 'export-toast' });
    } finally {
      setUsersLoading(false);
    }
  };

  // AI CONTEXT: admin-entry-management - Remove entry functionality
  // Reasoning: Allow admins to remove specific entries from giveaways
  // Constraints: Must handle both frontend and backend state updates
  // Future: Add audit trail for entry removals

  const handleRemoveEntry = async (entry) => {
    if (!selectedGiveaway) {
      toast.error('No giveaway selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove this entry? This action cannot be undone.`)) {
      return;
    }

    try {
      setEntriesLoading(true);
      
      const response = await authorizedFetch(`/admin/giveaways/${selectedGiveaway.id}/entries/${entry.slotNumber}`, {
        method: 'DELETE'
      });

      if (response && response.message) {
        toast.success('Entry removed successfully');
        
        // Remove entry from local state
        setSelectedGiveawayEntries(prev => 
          prev.filter(e => !(e.slotNumber === entry.slotNumber && e.email === entry.email))
        );
        
        // Refresh entries to get updated list
        await handleViewEntries(selectedGiveaway);
      } else {
        toast.error('Failed to remove entry');
      }
    } catch (error) {
      console.error('Error removing entry:', error);
      toast.error(error.message || 'Failed to remove entry');
    } finally {
      setEntriesLoading(false);
    }
  };

  // AI CONTEXT: admin-entry-management - Create private entry functionality
  // Reasoning: Allow admins to create private entries for mail-in orders
  // Constraints: Must create user account and entry atomically
  // Future: Add email notifications for new users

  const handleCreatePrivateEntry = async (formData) => {
    if (!selectedGiveaway) {
      toast.error('No giveaway selected');
      return;
    }

    try {
      setPrivateEntryLoading(true);
      
      const response = await authorizedFetch(`/admin/giveaways/${selectedGiveaway.id}/private-entries`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response && response.message) {
        let message = response.message;
        if (response.tempPassword) {
          message += ` Temporary password: ${response.tempPassword}`;
        }
        toast.success(message);
        
        // Close modal
        setShowPrivateEntryModal(false);
        
        // Refresh entries to show new private entry
        await handleViewEntries(selectedGiveaway);
      } else {
        toast.error('Failed to create private entry');
      }
    } catch (error) {
      console.error('Error creating private entry:', error);
      toast.error(error.message || 'Failed to create private entry');
    } finally {
      setPrivateEntryLoading(false);
    }
  };

  const handleCreateUser = async (formData) => {
    try {
      setUserCreationLoading(true);
      
      // For now, we'll use the same endpoint as private entries but without a giveaway
      // In the future, we could create a dedicated user creation endpoint
      const response = await authorizedFetch('/admin/users/create', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response && response.message) {
        toast.success(`User created successfully. Temporary password: ${response.tempPassword}`);
        
        // Close modal
        setShowUserCreationModal(false);
        
        // Refresh users list
        await loadUsers();
      } else {
        toast.error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    } finally {
      setUserCreationLoading(false);
    }
  };

  // AI CONTEXT: admin-security - Handle user selection for deletion
  // Reasoning: Allow admins to select multiple users for bulk deletion
  // Constraints: Must handle selection state properly
  // Future: Add select all/none functionality

  const handleUserSelection = (email) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  // AI CONTEXT: admin-security - Handle single user deletion
  // Reasoning: Allow admins to delete individual users
  // Constraints: Must confirm destructive action
  // Future: Add soft delete option

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleteLoading(true);
      const result = await authService.deleteUser(email);
      
      if (result.success) {
        // Remove user from local state
        setUsers(prev => prev.filter(user => user.email !== email));
        setSelectedUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(email);
          return newSet;
        });
        toast.success(`User ${email} deleted successfully`);
      } else {
        toast.error(`Failed to delete user: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(`Failed to delete user: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // AI CONTEXT: admin-security - Handle bulk user deletion
  // Reasoning: Allow admins to delete multiple users efficiently
  // Constraints: Must confirm destructive action, handle partial failures
  // Future: Add progress tracking for large batches

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) {
      toast.error('No users selected for deletion');
      return;
    }

    const selectedEmails = Array.from(selectedUsers);
    const confirmMessage = `Are you sure you want to delete ${selectedEmails.length} user(s)? This action cannot be undone.\n\nSelected users:\n${selectedEmails.join('\n')}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleteLoading(true);
      const result = await authService.deleteMultipleUsers(selectedEmails);
      
      if (result.success) {
        // Remove deleted users from local state
        setUsers(prev => prev.filter(user => !selectedUsers.has(user.email)));
        setSelectedUsers(new Set());
        toast.success(`Successfully deleted ${result.deletedCount} users`);
        
        if (result.failedCount > 0) {
          toast.error(`${result.failedCount} users failed to delete`);
        }
      } else {
        toast.error(`Failed to delete users: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting users:', error);
      toast.error(`Failed to delete users: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // AI CONTEXT: admin-security - Handle select all/none functionality
  // Reasoning: Allow admins to easily select all users for bulk operations
  // Constraints: Must handle selection state properly
  // Future: Add filtered selection (e.g., select all subscribers)

  const handleSelectAll = async () => {
    const isSearchActive = Boolean(userSearchTerm && userSearchTerm.trim());
    let targetUsers = [];

    if (isSearchActive) {
      targetUsers = filteredUsers;
    } else {
      if (allUsers.length > 0) {
        targetUsers = allUsers;
      } else {
        const loadedUsers = await loadAllUsersForSearch();
        if (Array.isArray(loadedUsers) && loadedUsers.length > 0) {
          targetUsers = loadedUsers;
        } else {
          targetUsers = users;
        }
      }
    }

    if (!Array.isArray(targetUsers) || targetUsers.length === 0) {
      targetUsers = users;
    }

    setSelectedUsers((prevSelected) => {
      const newSelectedUsers = new Set(prevSelected);
      const allSelected = targetUsers.every((user) => {
        const email = user?.email;
        return email ? newSelectedUsers.has(email) : false;
      });

      targetUsers.forEach((user) => {
        const email = user?.email;
        if (!email) return;

        if (allSelected) {
          newSelectedUsers.delete(email);
        } else {
          newSelectedUsers.add(email);
        }
      });

      return newSelectedUsers;
    });
  };

  // Feature flag: hide Cost Monitor tab until backend cost endpoint is implemented
  const showCostMonitor = false;
  const tabs = [
    { id: 'overview', name: t('admin.tabs.overview'), icon: TrendingUp },
    { id: 'giveaways', name: t('admin.tabs.giveaways'), icon: Gift },
    { id: 'blog', name: 'AI Blog', icon: FileText },
    { id: 'products', name: t('admin.tabs.products'), icon: ShoppingBag },
    { id: 'users', name: t('admin.tabs.users'), icon: Users },
    { id: 'referrals', name: 'Referrals', icon: UserPlus },
    { id: 'winners', name: t('admin.tabs.winners'), icon: Gift },
    { id: 'creditaudit', name: 'Credit Audit', icon: Shield },
    { id: 'support', name: 'Support Tickets', icon: AlertCircle },
    { id: 'inquiries', name: 'General Inquiries', icon: HelpCircle },
    { id: 'acquisition', name: t('admin.tabs.acquisition'), icon: Crosshair },
    { id: 'costs', name: t('admin.tabs.costs'), icon: Database, hidden: !showCostMonitor },
  ];

  const nowForActive = new Date();
  const isGiveawayActiveByDate = (g) => {
    if (!g) return false;
    if (!g.endDate) return true; // no end date means considered active until manually ended
    const d = new Date(g.endDate);
    return !Number.isNaN(d.getTime()) && d >= nowForActive;
  };

  // Check if giveaway ended more than 7 days ago (for past giveaways tab)
  const isGiveawayPastBy7Days = (g) => {
    if (!g) return false;
    if (!g.endDate) return false; // no end date means still active
    const endDate = new Date(g.endDate);
    if (Number.isNaN(endDate.getTime())) return false;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return endDate < sevenDaysAgo;
  };

  // Calculate subscriber count from all users (not just paginated)
  // Recalculate whenever allUsers or users change
  // NOTE: This hook is intentionally placed at the top level of the Admin component.
  // ESLint in the Amplify build environment was incorrectly flagging this as
  // conditionally called, even though it's not inside any conditional/loop.
  // We disable the specific rule here to keep the build green while preserving
  // the desired behavior and hook ordering.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const calculateSubscriberCount = () => {
      // Prefer allUsers if available (most complete data)
      let usersToCount = [];
      
      if (allUsers.length > 0) {
        usersToCount = allUsers;
      } else {
        // Try cached users
        const cachedUsers = getCachedUsers();
        if (cachedUsers && cachedUsers.length > 0) {
          usersToCount = cachedUsers;
        } else {
          // Fallback to paginated users
          usersToCount = users;
        }
      }
      
      const count = usersToCount.filter(user => 
        user.isSubscriber === true || (user.subscriptionTier && user.subscriptionTier !== 'Free')
      ).length;
      
      setSubscriberCount(count);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 Subscriber count calculated: ${count} from ${usersToCount.length} users (allUsers: ${allUsers.length}, cached: ${getCachedUsers()?.length || 0}, paginated: ${users.length})`);
      }
    };
    
    calculateSubscriberCount();
  }, [allUsers, users, getCachedUsers]);

  const stats = [
    { 
      name: t('admin.stats.totalUsers'), 
      value: allUsers.length > 0 ? allUsers.length : (totalUsers ?? users.length), 
      icon: Users 
    },
    { 
      name: 'Subscribers', 
      value: subscriberCount, 
      icon: UserPlus,
      // When clicking the Subscribers stat card, jump to the Users tab
      // and show only subscribers using the existing Users table UI.
      onClick: () => {
        setUserFilterMode('subscribers');
        setCurrentUserPage(1);
        setUserSearchTerm('');
        setActiveTab('users');
        // Ensure we have the latest paginated users loaded
        loadUsers();
      }
    },
    { name: t('admin.stats.activeGiveaways'), value: giveaways.filter(isGiveawayActiveByDate).length, icon: Gift },
    { name: t('admin.stats.products'), value: products.length, icon: ShoppingBag },
    { 
      name: t('admin.stats.revenue'), 
      value: revenueLoading ? 'Loading...' : `$${revenueData.total_revenue.toLocaleString()}`, 
      icon: DollarSign,
      subtitle: revenueData.period === 'all' ? 'All Time' : revenueData.period === 'month' ? 'This Month' : 'This Week'
    },
  ];

  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            {/* Header (tools hidden per request) */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-theme-silver mb-2">{t('admin.title')}</h1>
              <p className="text-base sm:text-lg text-theme-silver-light">{t('admin.subtitle')}</p>
            </div>
                {/* Hidden tools (time display / date selector) */}
                <div className="hidden" />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {tabs.filter(t => !t.hidden).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    // Reset user filter mode when navigating via tabs;
                    // Users tab defaults to showing all users unless coming from the Subscribers stat card.
                    if (tab.id !== 'users') {
                      setUserFilterMode('all');
                    }
                    setActiveTab(tab.id);
                  }}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Revenue Period Selector */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">Dashboard Overview</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => loadRevenueData('week')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                    revenueData.period === 'week'
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  This Week
                </button>
                <button
                  onClick={() => loadRevenueData('month')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                    revenueData.period === 'month'
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  This Month
                </button>
                <button
                  onClick={() => loadRevenueData('all')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                    revenueData.period === 'all'
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  All Time
                </button>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => {
                const Icon = stat.icon;
                const isClickable = typeof stat.onClick === 'function';
                return (
                  <div
                    key={stat.name}
                    className={`glass-card-dark p-6 ${isClickable ? 'cursor-pointer hover:border-custom-orange/50 border border-transparent' : ''}`}
                    onClick={stat.onClick}
                  >
                    <div className="flex items-center">
                      <div className="bg-custom-orange/20 rounded-lg p-3">
                        <Icon className="h-6 w-6 text-custom-orange" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-theme-silver-light">{stat.name}</p>
                        <p className="text-2xl font-bold text-theme-silver">{stat.value}</p>
                        {stat.subtitle && (
                          <p className="text-xs text-theme-silver-light">{stat.subtitle}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Revenue Details */}
            <div className="glass-card-dark p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-4">Revenue Details</h3>
              {revenueLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading revenue data...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-glass-white/5 rounded-lg">
                    <p className="text-sm text-theme-silver-light mb-2">Total Revenue</p>
                    <p className="text-2xl font-bold text-custom-orange">${revenueData.total_revenue.toLocaleString()}</p>
                  </div>
                  <div className="text-center p-4 bg-glass-white/5 rounded-lg">
                    <p className="text-sm text-theme-silver-light mb-2">Total Orders</p>
                    <p className="text-2xl font-bold text-theme-silver">{revenueData.total_orders}</p>
                  </div>
                  <div className="text-center p-4 bg-glass-white/5 rounded-lg">
                    <p className="text-sm text-theme-silver-light mb-2">Average Order Value</p>
                    <p className="text-2xl font-bold text-theme-silver">${revenueData.average_order_value.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="glass-card-dark p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-4">{t('admin.recentActivity')}</h3>
              {activityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading recent activity...</span>
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={`${activity.type}-${activity.timestamp}-${index}`} className="flex items-center justify-between p-4 bg-glass-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {activity.type === 'giveaway_entry' ? (
                          <Gift className="h-5 w-5 text-custom-orange" />
                        ) : activity.type === 'purchase' ? (
                          <DollarSign className="h-5 w-5 text-custom-orange" />
                        ) : activity.type === 'user_registration' ? (
                          <Users className="h-5 w-5 text-custom-orange" />
                        ) : (
                          <TrendingUp className="h-5 w-5 text-custom-orange" />
                        )}
                        <div>
                          <p className="font-medium text-theme-silver">{activity.title}</p>
                          <p className="text-sm text-theme-silver-light">{activity.description}</p>
                        </div>
                      </div>
                      <span className="text-sm text-theme-silver-light">
                        {formatRelativeTime(activity.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Recent Activity</h3>
                  <p className="text-theme-silver-light">No activity has been recorded in the last 24 hours.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Giveaways Tab */}
        {activeTab === 'giveaways' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">{t('admin.manageGiveaways')}</h2>
              <div className="flex space-x-2">
                <button
                  onClick={debugEntriesAPI}
                  className="btn-secondary text-sm"
                >
                  Debug Entries
                </button>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>{t('admin.createGiveaway')}</span>
                </button>
              </div>
            </div>

            {/* Active/Past Giveaways Tabs */}
            <div className="flex gap-2">
              {[
                { id: 'active', label: 'Active Giveaways' },
                { id: 'past', label: 'Past Giveaways' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setGiveawayListTab(t.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    giveawayListTab === t.id
                      ? 'bg-custom-orange text-white'
                      : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {giveaways
                .filter(g => {
                  if (giveawayListTab === 'active') {
                    // Show active giveaways (not ended or ended less than 7 days ago)
                    return !isGiveawayPastBy7Days(g);
                  } else {
                    // Show past giveaways (ended more than 7 days ago)
                    return isGiveawayPastBy7Days(g);
                  }
                })
                .map((giveaway) => (
                <div key={giveaway.id} className="glass-card-dark p-6">
                  <div className="aspect-video glass-card rounded-lg mb-4 overflow-hidden flex items-center justify-center">
                    <S3Image
                      s3KeyOrUrl={giveaway.image || '/files/Logo.png'}
                      alt={giveaway.title}
                      className="w-full h-full max-w-full max-h-full object-contain p-4"
                      fallback="/files/Logo.png"
                    />
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-theme-silver">{giveaway.title}</h3>
                    <span className={`tier-badge tier-${(giveaway?.tier || 'Bronze').toLowerCase()}`}>
                      {giveaway?.tier || 'Bronze'}
                    </span>
                  </div>
                  <p className="text-theme-silver-light mb-4 text-sm">{giveaway.description}</p>
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.value')}</span>
                      <span className="font-semibold text-theme-silver">${Number(giveaway?.value ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.entries')}</span>
                      <span className="font-semibold text-theme-silver">{giveaway?.entries?.length ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.available')}</span>
                      <span className="font-semibold text-theme-silver">{giveaway?.availableSlots ?? 0}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditGiveaway(giveaway)}
                      className="btn-secondary flex-1 text-sm"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {t('admin.edit')}
                    </button>
                    <button
                      onClick={() => handleDeleteGiveaway(giveaway.id)}
                      className="btn-secondary flex-1 text-sm"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('admin.delete')}
                    </button>
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => handleViewEntries(giveaway)}
                      className="btn-secondary flex-1 text-sm"
                    >
                      <Users className="h-4 w-4 mr-1" />
                      View Entries
                    </button>
                    {(giveaway?.entries?.length ?? 0) > 0 && (
                      <button
                        onClick={() => handleSelectWinner(giveaway.id)}
                        className="btn-primary flex-1 text-sm"
                      >
                        {t('admin.selectWinner')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">{t('admin.manageProducts')}</h2>
              <button
                onClick={() => setShowCreateProduct(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>{t('admin.createProduct')}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div key={product.id} className="glass-card-dark p-6">
                  <div className="aspect-square glass-card rounded-lg mb-4 overflow-hidden flex items-center justify-center">
                    <S3Image
                      s3KeyOrUrl={product.image || '/files/Logo.png'}
                      alt={product.name}
                      className="w-full h-full max-w-full max-h-full object-contain p-4"
                      fallback="/files/Logo.png"
                    />
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-theme-silver">{product.name}</h3>
                    <span className={`tier-badge tier-${(product.tier || 'Bronze').toLowerCase()}`}>{product.tier}</span>
                  </div>
                  <p className="text-theme-silver-light mb-4 text-sm">{product.description}</p>
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.price')}</span>
                      <div className="text-right">
                        {product.salesPrice ? (
                          <div>
                            <span className="font-semibold text-custom-orange line-through">${Number(product.price).toLocaleString()}</span>
                            <span className="font-semibold text-theme-silver ml-2">${Number(product.salesPrice).toLocaleString()}</span>
                          </div>
                        ) : (
                          <span className="font-semibold text-theme-silver">${Number(product.price).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.entries')}</span>
                      <span className="font-semibold text-theme-silver">{product.entries}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-theme-silver-light">{t('admin.inStock')}</span>
                      <span className="font-semibold text-theme-silver">{product.inStock ? 'Yes' : 'No'}</span>
                    </div>
                    {product.displayOrder !== null && product.displayOrder !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-theme-silver-light">Display Order</span>
                        <span className="font-semibold text-theme-silver">#{product.displayOrder}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleEditProduct(product)} className="btn-secondary flex-1 text-sm"><Edit className="h-4 w-4 mr-1"/>Edit</button>
                    <button onClick={() => deleteProduct(product.id)} className="btn-secondary flex-1 text-sm"><Trash2 className="h-4 w-4 mr-1"/>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-theme-silver">{t('admin.userManagement')}</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    loadUsers(
                      typeof currentUserPage === 'number' && currentUserPage >= 1 ? currentUserPage : 1,
                      false
                    )
                  }
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                  disabled={usersLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{t('admin.refresh')}</span>
                </button>
                <button
                  onClick={refreshUserCache}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                  disabled={usersLoading}
                  title="Refresh cache for instant pagination"
                >
                  <RefreshCw className={`h-4 w-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Cache</span>
                </button>
                <button
                  onClick={exportUserData}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('admin.exportData')}</span>
                </button>
                <button
                  onClick={() => setShowUserCreationModal(true)}
                  className="px-3 py-2 bg-theme-bronze hover:bg-theme-bronze/80 text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                >
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Create User</span>
                </button>
                <button
                  onClick={async () => {
                    // Clear selection to indicate "select all"
                    setSelectedUsers(new Set());
                    // Always reload all users to ensure we have the latest data
                    const loadedUsers = await loadAllUsersForSearch();
                    console.log(`🔍 SMS All: Loaded ${loadedUsers ? loadedUsers.length : 0} users`);
                    setShowSmsModal(true);
                  }}
                  className="px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                  title="Send SMS to ALL users (will fetch all users from database)"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">SMS All</span>
                </button>
                {selectedUsers.size > 0 && (
                  <button
                    onClick={async () => {
                      // Ensure all users are loaded for SMS modal
                      if (allUsers.length === 0) {
                        await loadAllUsersForSearch();
                      }
                      setShowSmsModal(true);
                    }}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                    title={`Send SMS to ${selectedUsers.size} selected users`}
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span className="hidden sm:inline">SMS Selected ({selectedUsers.size})</span>
                  </button>
                )}
                {selectedUsers.size > 0 && (
                  <button
                    onClick={handleBulkDeleteUsers}
                    disabled={deleteLoading}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Delete {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}</span>
                    <span className="sm:hidden">Delete</span>
                  </button>
                )}
              </div>
            </div>

            {/* Search Users */}
            <div className="glass-card-dark p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label htmlFor="userSearch" className="block text-sm font-medium text-theme-silver mb-2">
                    Search Users
                  </label>
                  <div className="relative">
                    <input
                      id="userSearch"
                      type="text"
                      placeholder="Search all users by name, email, or phone number..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      disabled={searchLoading}
                      className="w-full px-4 py-2 pl-10 bg-theme-black border border-theme-silver/30 rounded-lg text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-theme-bronze focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {searchLoading ? (
                      <RefreshCw className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-theme-silver-light animate-spin" />
                    ) : (
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-theme-silver-light" />
                    )}
                  </div>
                </div>
                {userSearchTerm && (
                  <button
                    onClick={() => setUserSearchTerm('')}
                    className="btn-secondary px-4 py-2 text-sm"
                  >
                    Clear
                  </button>
                )}
              </div>
              {userSearchTerm && (
                <div className="mt-2 text-sm text-theme-silver-light">
                  {searchLoading ? (
                    <>Loading all users for search...</>
                  ) : (
                    <>Showing {filteredUsers.length} of {allUsers.length > 0 ? allUsers.length : users.length} users</>
                  )}
                </div>
              )}
            </div>

            {selectedUsers.size > 0 && (
              <div className="glass-card-dark p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-theme-silver font-medium">
                      {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => setSelectedUsers(new Set())}
                      className="text-theme-silver-light hover:text-theme-silver text-sm"
                    >
                      Clear Selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteUsers}
                    disabled={deleteLoading}
                    className="btn-primary bg-red-600 hover:bg-red-700 flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Selected</span>
                  </button>
                </div>
              </div>
            )}
            
            <div className="glass-card-dark">
              {usersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">{t('admin.loadingUsers')}</span>
                </div>
              ) : filteredUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  {(() => {
                    const startIndex = (currentUserPage - 1) * usersPerPage;
                    const endIndex = startIndex + usersPerPage;
                    const visibleUsers = !userSearchTerm.trim()
                      ? filteredUsers.slice(startIndex, endIndex)
                      : filteredUsers;

                    return (
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-theme-silver/20">
                            <th className="text-left py-3 px-4 font-semibold text-theme-silver">
                              <input
                                type="checkbox"
                                checked={
                                  visibleUsers.length > 0 &&
                                  visibleUsers.every((user) => selectedUsers.has(user.email))
                                }
                                onChange={handleSelectAll}
                                className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                              />
                            </th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">{t('admin.table.name')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">{t('admin.table.email')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">Subscription</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">Credits</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">Active Entries</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">{t('admin.table.purchases')}</th>
                        <th className="text-left py-3 px-4 font-semibold text-theme-silver">{t('admin.table.joinDate')}</th>
                            <th className="text-left py-3 px-4 font-semibold text-theme-silver">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleUsers.map((user) => (
                            <tr key={user.id} className="border-b border-theme-silver/10">
                          <td className="py-3 px-4 text-theme-silver">
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.email)}
                              onChange={() => handleUserSelection(user.email)}
                              className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                            />
                          </td>
                          <td className="py-3 px-4 text-theme-silver">
                            <div className="flex items-center space-x-2">
                              <span>{user.name}</span>
                              {user.isAdmin && (
                                  <span className="bg-custom-orange text-white text-xs px-2 py-1 rounded-full">
                                   {t('admin.badge.admin')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-theme-silver-light">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              user.subscriptionTier === 'Free'
                                ? 'bg-gray-500/20 text-gray-400'
                                : user.subscriptionTier === 'Bronze'
                                ? 'bg-amber-500/20 text-amber-400'
                                : user.subscriptionTier === 'Silver'
                                ? 'bg-gray-400/20 text-gray-300'
                                : user.subscriptionTier === 'Gold'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : user.subscriptionTier === 'Platinum'
                                ? 'bg-purple-500/20 text-purple-400'
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {user.subscriptionTier || (user.isSubscriber ? 'Subscriber' : 'Free')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-theme-silver font-semibold">
                            {(() => {
                              const credits = user.credits && typeof user.credits === 'object' ? user.credits : {};
                              const total = Number(credits.total ?? credits.totalCredits ?? 0) || 0;
                              const bronze = Number(credits.bronze ?? credits.bronzeCredits ?? 0) || 0;
                              const silver = Number(credits.silver ?? credits.silverCredits ?? 0) || 0;
                              const gold = Number(credits.gold ?? credits.goldCredits ?? 0) || 0;
                              const platinum = Number(credits.platinum ?? credits.platinumCredits ?? 0) || 0;
                              return (
                              <div className="text-sm">
                                <div className="font-bold text-custom-orange">{total}</div>
                                <div className="text-xs text-theme-silver-light space-x-1">
                                  <span>B: {bronze}</span>
                                  <span>S: {silver}</span>
                                  <span>G: {gold}</span>
                                  <span>P: {platinum}</span>
                                </div>
                              </div>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4 text-theme-silver font-semibold">
                            <span className="text-custom-orange">{Number(user.entries || user.activeEntries || 0) || 0}</span>
                            <div className="text-xs text-theme-silver-light">Live giveaways</div>
                          </td>
                          <td className="py-3 px-4 text-theme-silver-light">{Number(user.purchases || 0) || 0}</td>
                          <td className="py-3 px-4 text-theme-silver-light">{formatDate(user.joinDate, { includeTime: false })}</td>
                              <td className="py-3 px-4">
                                <div className="flex space-x-2">
                              <button
                                onClick={() => loadUserProfile(user.email)}
                                disabled={profileLoading}
                                className="btn-secondary text-xs px-2 py-1 flex items-center space-x-1"
                              >
                                <Eye className="h-3 w-3" />
                                <span>Profile</span>
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.email)}
                                disabled={deleteLoading}
                                className="btn-secondary text-xs px-2 py-1 flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white"
                              >
                                <Trash2 className="h-3 w-3" />
                                <span>Delete</span>
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  const tier = window.prompt('Tier (Bronze/Silver/Gold/Platinum)?', 'Bronze');
                                  if (!tier) return;
                                  const amountStr = window.prompt('Amount to grant (positive integer)?', '1');
                                  const amount = parseInt(amountStr || '0', 10);
                                  if (!amount || amount <= 0) return;
                                  try {
                                    const res = await creditService.grantCreditsByEmail(user.email, amount, tier);
                                    if (res) {
                                      toast.success(`Granted ${amount} ${tier} credit(s) to ${user.email}`);
                                      loadUsers(currentUserPage);
                                    }
                                  } catch (err) {
                                    console.error(err);
                                    toast.error('Failed to grant credits');
                                  }
                                }}
                                className="btn-secondary text-xs px-2 py-1 flex items-center space-x-1"
                              >
                                <Plus className="h-3 w-3" />
                                <span>Grant</span>
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  const tier = window.prompt('Tier to subtract (Bronze/Silver/Gold/Platinum)?', 'Bronze');
                                  if (!tier) return;
                                  const amountStr = window.prompt('Amount to subtract (positive integer)?', '1');
                                  const amount = parseInt(amountStr || '0', 10);
                                  if (!amount || amount <= 0) return;
                                  try {
                                    const res = await creditService.adjustCreditsByEmail(user.email, amount, tier, 'subtract');
                                    if (res && res.statusCode !== 409) {
                                      toast.success(`Subtracted ${amount} ${tier} credit(s) from ${user.email}`);
                                      loadUsers(currentUserPage);
                                    } else if (res && res.statusCode === 409) {
                                      toast.error('Insufficient credits to subtract');
                                    }
                                  } catch (err) {
                                    console.error(err);
                                    toast.error('Failed to subtract credits');
                                  }
                                }}
                                className="btn-secondary text-xs px-2 py-1 flex items-center space-x-1"
                              >
                                <Minus className="h-3 w-3" />
                                <span>Subtract</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  {userSearchTerm ? (
                    <>
                      <h3 className="text-lg font-semibold text-theme-silver mb-2">No users found</h3>
                      <p className="text-theme-silver-light">No users match your search criteria. Try adjusting your search terms.</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-theme-silver mb-2">{t('admin.noUsers')}</h3>
                      <p className="text-theme-silver-light">{t('admin.noUsersHelp')}</p>
                    </>
                  )}
                </div>
              )}
              
              {/* Pagination Controls - Always show if there are users */}
              {filteredUsers.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-theme-silver/20">
                  <div className="text-sm text-theme-silver-light">
                    {userSearchTerm ? (
                      <>Showing {filteredUsers.length} of {allUsers.length > 0 ? allUsers.length : users.length} users</>
                    ) : (
                      <>Showing {Math.min(filteredUsers.length, ((currentUserPage - 1) * usersPerPage) + 1)}-{Math.min(currentUserPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users</>
                    )}
                  </div>
                  {!userSearchTerm && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={loadPreviousPage}
                        disabled={currentUserPage <= 1 || usersLoading}
                        className="btn-secondary px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-theme-silver px-3 py-1 text-sm">
                        Page {currentUserPage}
                      </span>
                      <button
                        onClick={loadNextPage}
                        disabled={currentUserPage >= (Math.ceil(filteredUsers.length / usersPerPage) || 1) || usersLoading}
                        className="btn-secondary px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Support Tickets Tab */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">Support Tickets</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSupportTickets}
                  className="btn-secondary flex items-center space-x-2"
                  disabled={supportLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${supportLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            <div className="glass-card-dark">
              {supportLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading support tickets...</span>
                </div>
              ) : supportTickets.length > 0 ? (
                <div className="space-y-4">
                  {supportTickets.map((ticket) => (
                    <div key={ticket.id} className="border border-theme-silver/20 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-theme-silver">{ticket.subject}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              ticket.priority === 'high' ? 'bg-red-500/20 text-red-400 border border-red-400/30' :
                              ticket.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/30' :
                              'bg-green-500/20 text-green-400 border border-green-400/30'
                            }`}>
                              {ticket.priority.toUpperCase()}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              ticket.status === 'open' ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' :
                              ticket.status === 'in-progress' ? 'bg-orange-500/20 text-orange-400 border border-orange-400/30' :
                              ticket.status === 'closed' ? 'bg-gray-500/20 text-gray-400 border border-gray-400/30' :
                              'bg-purple-500/20 text-purple-400 border border-purple-400/30'
                            }`}>
                              {ticket.status}
                            </span>
                          </div>
                          <p className="text-theme-silver-light mb-3">{ticket.message}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-theme-silver-light">User: <span className="text-theme-silver">{ticket.userName}</span></p>
                              <p className="text-theme-silver-light">Email: <span className="text-theme-silver">{ticket.userEmail}</span></p>
                              {ticket.userPhone && (
                                <p className="text-theme-silver-light">Phone: <span className="text-theme-silver">{ticket.userPhone}</span></p>
                              )}
                            </div>
                            <div>
                              <p className="text-theme-silver-light">Created: <span className="text-theme-silver">{new Date(ticket.createdAt).toLocaleString()}</span></p>
                              <p className="text-theme-silver-light">Updated: <span className="text-theme-silver">{new Date(ticket.updatedAt).toLocaleString()}</span></p>
                              {ticket.assignedTo && (
                                <p className="text-theme-silver-light">Assigned to: <span className="text-theme-silver">{ticket.assignedTo}</span></p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <select
                          value={ticket.status}
                          onChange={async (e) => {
                            try {
                              const response = await authorizedFetch(`/admin/support/tickets/${ticket.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ status: e.target.value })
                              });
                              if (response && response.success) {
                                toast.success('Ticket status updated');
                                loadSupportTickets();
                              }
                            } catch (error) {
                              console.error('Error updating ticket status:', error);
                              toast.error('Failed to update ticket status');
                            }
                          }}
                          className="glass-input text-sm"
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="closed">Closed</option>
                        </select>
                        
                        <input
                          type="text"
                          placeholder="Assign to..."
                          value={ticket.assignedTo || ''}
                          onChange={async (e) => {
                            try {
                              const response = await authorizedFetch(`/admin/support/tickets/${ticket.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ assignedTo: e.target.value || null })
                              });
                              if (response && response.success) {
                                toast.success('Ticket assignment updated');
                                loadSupportTickets();
                              }
                            } catch (error) {
                              console.error('Error updating ticket assignment:', error);
                              toast.error('Failed to update ticket assignment');
                            }
                          }}
                          className="glass-input text-sm flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Support Tickets</h3>
                  <p className="text-theme-silver-light">No support tickets have been submitted yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* General Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">General Inquiries</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadGeneralInquiries}
                  className="btn-secondary flex items-center space-x-2"
                  disabled={inquiriesLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${inquiriesLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>

            <div className="glass-card-dark">
              {inquiriesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading general inquiries...</span>
                </div>
              ) : generalInquiries.length > 0 ? (
                <div className="space-y-4">
                  {generalInquiries.map((inquiry) => (
                    <div key={inquiry.id} className="border border-theme-silver/20 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-theme-silver">{inquiry.subject}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              inquiry.status === 'new' ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' :
                              inquiry.status === 'reviewed' ? 'bg-green-500/20 text-green-400 border border-green-400/30' :
                              inquiry.status === 'closed' ? 'bg-gray-500/20 text-gray-400 border border-gray-400/30' :
                              'bg-purple-500/20 text-purple-400 border border-purple-400/30'
                            }`}>
                              {inquiry.status}
                            </span>
                          </div>
                          <p className="text-theme-silver-light mb-3">{inquiry.message}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-theme-silver-light">User: <span className="text-theme-silver">{inquiry.userName}</span></p>
                              <p className="text-theme-silver-light">Email: <span className="text-theme-silver">{inquiry.userEmail}</span></p>
                              {inquiry.userPhone && (
                                <p className="text-theme-silver-light">Phone: <span className="text-theme-silver">{inquiry.userPhone}</span></p>
                              )}
                            </div>
                            <div>
                              <p className="text-theme-silver-light">Created: <span className="text-theme-silver">{new Date(inquiry.createdAt).toLocaleString()}</span></p>
                              <p className="text-theme-silver-light">Updated: <span className="text-theme-silver">{new Date(inquiry.updatedAt).toLocaleString()}</span></p>
                              {inquiry.assignedTo && (
                                <p className="text-theme-silver-light">Assigned to: <span className="text-theme-silver">{inquiry.assignedTo}</span></p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <select
                          value={inquiry.status}
                          onChange={async (e) => {
                            try {
                              const response = await authorizedFetch(`/admin/support/inquiries/${inquiry.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ status: e.target.value })
                              });
                              if (response && response.success) {
                                toast.success('Inquiry status updated');
                                loadGeneralInquiries();
                              }
                            } catch (error) {
                              console.error('Error updating inquiry status:', error);
                              toast.error('Failed to update inquiry status');
                            }
                          }}
                          className="glass-input text-sm"
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="closed">Closed</option>
                        </select>
                        
                        <input
                          type="text"
                          placeholder="Assign to..."
                          value={inquiry.assignedTo || ''}
                          onChange={async (e) => {
                            try {
                              const response = await authorizedFetch(`/admin/support/inquiries/${inquiry.id}`, {
                                method: 'PATCH',
                                body: JSON.stringify({ assignedTo: e.target.value || null })
                              });
                              if (response && response.success) {
                                toast.success('Inquiry assignment updated');
                                loadGeneralInquiries();
                              }
                            } catch (error) {
                              console.error('Error updating inquiry assignment:', error);
                              toast.error('Failed to update inquiry assignment');
                            }
                          }}
                          className="glass-input text-sm flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <HelpCircle className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No General Inquiries</h3>
                  <p className="text-theme-silver-light">No general inquiries have been submitted yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Acquisition Requests Tab */}
        {activeTab === 'acquisition' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">Acquisition Requests</h2>
              <button
                onClick={loadAcquisitionRequests}
                className="btn-secondary flex items-center space-x-2"
                disabled={acquisitionLoading}
              >
                <RefreshCw className={`h-4 w-4 ${acquisitionLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            <div className="glass-card-dark">
              {acquisitionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading acquisition requests...</span>
                </div>
              ) : acquisitionRequests.length > 0 ? (
                <div className="space-y-4 p-4">
                  {acquisitionRequests.map((request) => {
                    const address = request.address || {};
                    const addressLine = [address.line1, address.line2, address.city, address.state, address.postalCode]
                      .filter(Boolean)
                      .join(', ');
                    return (
                      <div key={request.id} className="border border-theme-silver/20 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-theme-silver">{request.fullName}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                request.status === 'new' ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' :
                                request.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/30' :
                                request.status === 'sourcing' ? 'bg-green-500/20 text-green-400 border border-green-400/30' :
                                'bg-gray-500/20 text-gray-400 border border-gray-400/30'
                              }`}>
                                {request.status}
                              </span>
                            </div>
                            {request.firearmDetails && (
                              <p className="text-theme-silver-light mb-3 whitespace-pre-wrap">{request.firearmDetails}</p>
                            )}
                            {request.sourceGiveaway && (
                              <p className="text-sm text-custom-orange mb-3">From giveaway: {request.sourceGiveaway}</p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-theme-silver-light">Email: <span className="text-theme-silver">{request.email}</span></p>
                                <p className="text-theme-silver-light">Phone: <span className="text-theme-silver">{request.phone}</span></p>
                                {addressLine && (
                                  <p className="text-theme-silver-light">Address: <span className="text-theme-silver">{addressLine}</span></p>
                                )}
                              </div>
                              <div>
                                <p className="text-theme-silver-light">Created: <span className="text-theme-silver">{request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}</span></p>
                                <p className="text-theme-silver-light">Updated: <span className="text-theme-silver">{request.updatedAt ? new Date(request.updatedAt).toLocaleString() : '—'}</span></p>
                                {request.assignedTo && (
                                  <p className="text-theme-silver-light">Assigned to: <span className="text-theme-silver">{request.assignedTo}</span></p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <select
                            value={request.status || 'new'}
                            onChange={async (e) => {
                              try {
                                const response = await updateAdminAcquisitionRequest(request.id, { status: e.target.value });
                                if (response && response.success) {
                                  toast.success('Request status updated');
                                  loadAcquisitionRequests();
                                }
                              } catch (error) {
                                console.error('Error updating acquisition status:', error);
                                toast.error('Failed to update request status');
                              }
                            }}
                            className="glass-input text-sm"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="sourcing">Sourcing</option>
                            <option value="closed">Closed</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Assign to..."
                            defaultValue={request.assignedTo || ''}
                            onBlur={async (e) => {
                              const value = e.target.value.trim() || null;
                              if ((request.assignedTo || null) === value) return;
                              try {
                                const response = await updateAdminAcquisitionRequest(request.id, { assignedTo: value });
                                if (response && response.success) {
                                  toast.success('Assignment updated');
                                  loadAcquisitionRequests();
                                }
                              } catch (error) {
                                console.error('Error updating acquisition assignment:', error);
                                toast.error('Failed to update assignment');
                              }
                            }}
                            className="glass-input text-sm flex-1"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Crosshair className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Acquisition Requests</h3>
                  <p className="text-theme-silver-light">Firearm sourcing requests will appear here.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Blog Tab */}
        {activeTab === 'blog' && (
          <AdminBlogTab />
        )}

        {/* Winners Tab */}
        {activeTab === 'winners' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">Giveaway Winners</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadWinners}
                  className="btn-secondary flex items-center space-x-2"
                  disabled={winnersLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${winnersLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await authorizedFetch('/admin/check-winners', { method: 'POST' });
                      if (response) {
                        toast.success(`Processed ${response.processed} giveaways, selected ${response.winnersSelected} winners`);
                        loadWinners(); // Refresh the winners list
                      }
                    } catch (error) {
                      console.error('Error checking winners:', error);
                      toast.error('Failed to check for winners');
                    }
                  }}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Gift className="h-4 w-4" />
                  <span>Check for Winners</span>
                </button>
              </div>
            </div>

            <div className="glass-card-dark">
              {winnersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                  <span className="ml-3 text-theme-silver">Loading winners...</span>
                </div>
              ) : winners.length > 0 ? (
                <div className="space-y-6">
                  {winners.map((giveawayWinners) => (
                    <div key={giveawayWinners.giveawayId} className="border border-theme-silver/20 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-theme-silver">{giveawayWinners.giveaway.title}</h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className={`tier-badge tier-${(giveawayWinners.giveaway.tier || 'Bronze').toLowerCase()}`}>
                              {giveawayWinners.giveaway.tier}
                            </span>
                            <span className="text-theme-silver-light">Value: ${giveawayWinners.giveaway.value}</span>
                            <span className="text-theme-silver-light">Ended: {formatDate(giveawayWinners.giveaway.endDate)}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-sm text-theme-silver-light">Total Winners</p>
                            <p className="text-2xl font-bold text-custom-orange">{giveawayWinners.totalWinners}</p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Are you sure you want to cancel the winner for "${giveawayWinners.giveaway.title}"? This will remove the winner and allow a new selection.`)) {
                                return;
                              }
                              try {
                                const response = await authorizedFetch('/admin/cancel-winner', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ giveawayId: giveawayWinners.giveawayId })
                                });
                                if (response) {
                                  toast.success(`Winner canceled for "${giveawayWinners.giveaway.title}"`);
                                  loadWinners(); // Refresh the winners list
                                  fetchGiveaways(); // Refresh giveaways to update status
                                }
                              } catch (error) {
                                console.error('Error canceling winner:', error);
                                toast.error(error.message || 'Failed to cancel winner');
                              }
                            }}
                            className="btn-secondary flex items-center space-x-2 px-3 py-2 text-sm"
                            title="Cancel Winner"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>Cancel Winner</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {giveawayWinners.winners.map((winner) => (
                          <div key={winner.id} className="glass-card p-4 border border-theme-silver/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-lg font-bold text-custom-orange">
                                Slot {winner.slotNumber}
                              </span>
                              <span className={`tier-badge tier-${(winner.tier || 'Bronze').toLowerCase()}`}>
                                {winner.tier}
                              </span>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="text-theme-silver font-medium truncate">
                                {winner.email}
                              </p>
                              <p className="text-theme-silver-light">
                                Selected: {formatDate(winner.selectedAt)}
                              </p>
                              <p className="text-theme-silver-light">
                                Method: {winner.method}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Gift className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Winners Yet</h3>
                  <p className="text-theme-silver-light">Winners will appear here once giveaways end and winners are automatically selected.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h2 className="text-2xl font-bold text-theme-silver">Referral System Dashboard</h2>
              <button
                onClick={loadReferralsData}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center space-x-2 transition-colors"
                disabled={referralsLoading}
              >
                <RefreshCw className={`h-4 w-4 ${referralsLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            {/* Statistics Cards */}
            {referralsData.statistics && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glass-card-dark p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-theme-silver-light text-sm">Total Referrals</p>
                    <UserPlus className="h-5 w-5 text-custom-orange" />
                  </div>
                  <p className="text-3xl font-bold text-theme-silver">
                    {referralsData.statistics.totalReferrals || 0}
                  </p>
                </div>

                <div className="glass-card-dark p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-theme-silver-light text-sm">Credits Issued</p>
                    <DollarSign className="h-5 w-5 text-green-400" />
                  </div>
                  <p className="text-3xl font-bold text-green-400">
                    {referralsData.statistics.creditsIssued || 0}
                  </p>
                </div>

                <div className="glass-card-dark p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-theme-silver-light text-sm">Credits Pending</p>
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <p className="text-3xl font-bold text-yellow-400">
                    {referralsData.statistics.creditsPending || 0}
                  </p>
                </div>

                <div className="glass-card-dark p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-theme-silver-light text-sm">Unique Referrers</p>
                    <Users className="h-5 w-5 text-custom-orange" />
                  </div>
                  <p className="text-3xl font-bold text-theme-silver">
                    {referralsData.statistics.uniqueReferrers || 0}
                  </p>
                </div>
              </div>
            )}

            {/* Top Referrers */}
            {referralsData.statistics?.topReferrers && referralsData.statistics.topReferrers.length > 0 && (
              <div className="glass-card-dark p-6">
                <h3 className="text-lg font-semibold text-theme-silver mb-4 flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-custom-orange" />
                  <span>Top Referrers</span>
                </h3>
                <div className="space-y-2">
                  {referralsData.statistics.topReferrers.slice(0, 5).map((referrer, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-theme-black/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-custom-orange font-bold">#{index + 1}</span>
                        <span className="text-theme-silver">{referrer.email}</span>
                      </div>
                      <span className="text-theme-silver-light font-semibold">{referrer.count} referrals</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Backfill Referral */}
            <div className="glass-card-dark p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-4 flex items-center space-x-2">
                <UserPlus className="h-5 w-5 text-custom-orange" />
                <span>Backfill Missing Referral</span>
              </h3>
              <form onSubmit={handleBackfillReferral} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">
                      Referrer Email (optional)
                    </label>
                    <input
                      type="email"
                      value={backfillReferralForm.referrerEmail}
                      onChange={(e) =>
                        setBackfillReferralForm(prev => ({ ...prev, referrerEmail: e.target.value }))
                      }
                      placeholder="chase@example.com"
                      className="w-full px-4 py-2 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">
                      Referred User Email (required)
                    </label>
                    <input
                      type="email"
                      required
                      value={backfillReferralForm.referredUserEmail}
                      onChange={(e) =>
                        setBackfillReferralForm(prev => ({ ...prev, referredUserEmail: e.target.value }))
                      }
                      placeholder="brittney@example.com"
                      className="w-full px-4 py-2 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">
                      Referral Code (optional)
                    </label>
                    <input
                      type="text"
                      value={backfillReferralForm.referralCode}
                      onChange={(e) =>
                        setBackfillReferralForm(prev => ({ ...prev, referralCode: e.target.value }))
                      }
                      placeholder="12345678"
                      className="w-full px-4 py-2 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-theme-silver-light">
                    Provide a referrer email or a referral code. The system will record the referral and issue credits if needed.
                  </p>
                  <button
                    type="submit"
                    disabled={backfillReferralLoading}
                    className="px-4 py-2 bg-custom-orange hover:bg-orange-500 text-black font-semibold rounded-lg transition-colors disabled:opacity-60"
                  >
                    {backfillReferralLoading ? 'Backfilling...' : 'Backfill Referral'}
                  </button>
                </div>
              </form>
            </div>

            {/* Search and Filters */}
            <div className="glass-card-dark p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-silver-light" />
                  <input
                    type="text"
                    placeholder="Search by referrer email, referred user email, or referral code..."
                    value={referralsSearchTerm}
                    onChange={(e) => setReferralsSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50"
                  />
                </div>
              </div>
            </div>

            {/* Referrals List */}
            <div className="glass-card-dark overflow-hidden">
              {referralsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 text-custom-orange animate-spin" />
                </div>
              ) : referralsData.referrals && referralsData.referrals.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-theme-black/50">
                      <tr>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Referrer
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Code
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Referred User
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Registered
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Credit Status
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-theme-silver uppercase tracking-wider">
                          Audit
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-theme-silver/10">
                      {referralsData.referrals
                        .filter(ref => {
                          if (!referralsSearchTerm) return true;
                          const search = referralsSearchTerm.toLowerCase();
                          return (
                            ref.referrerEmail?.toLowerCase().includes(search) ||
                            ref.referredUserEmail?.toLowerCase().includes(search) ||
                            ref.referralCode?.toLowerCase().includes(search) ||
                            ref.referrerName?.toLowerCase().includes(search) ||
                            ref.referredUserName?.toLowerCase().includes(search)
                          );
                        })
                        .map((referral, index) => (
                          <tr key={index} className="hover:bg-theme-black/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="text-sm">
                                <div className="font-medium text-theme-silver">{referral.referrerName}</div>
                                <div className="text-xs text-theme-silver-light">{referral.referrerEmail}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-custom-orange/20 text-custom-orange font-mono text-sm rounded">
                                {referral.referralCode}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-sm">
                                <div className="font-medium text-theme-silver">{referral.referredUserName}</div>
                                <div className="text-xs text-theme-silver-light">{referral.referredUserEmail}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-theme-silver-light">
                              {referral.registeredAt ? formatDate(referral.registeredAt, { includeTime: false }) : 'N/A'}
                            </td>
                            <td className="py-3 px-4">
                              {referral.creditIssued ? (
                                <div className="flex items-center space-x-2 text-green-400">
                                  <Shield className="h-4 w-4" />
                                  <span className="text-sm font-medium">Issued</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2 text-yellow-400">
                                  <AlertCircle className="h-4 w-4" />
                                  <span className="text-sm font-medium">Pending</span>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {referral.creditAuditRecord ? (
                                <div className="text-xs">
                                  <div className="flex items-center space-x-1 text-green-400 mb-1">
                                    <Shield className="h-3 w-3" />
                                    <span className="font-medium">{referral.creditAuditRecord.status}</span>
                                  </div>
                                  {referral.creditAuditRecord.creditsAwarded && (
                                    <div className="text-theme-silver-light">
                                      Bronze: {referral.creditAuditRecord.creditsAwarded.Bronze || 0}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-theme-silver-light">No audit record</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <UserPlus className="h-12 w-12 text-theme-silver mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold text-theme-silver mb-2">No Referrals Yet</h3>
                  <p className="text-theme-silver-light">
                    Referrals will appear here when users register with referral codes.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Credit Audit Tab */}
        {activeTab === 'creditaudit' && (
          <CreditAuditTab />
        )}

        {/* Cost Monitor Tab */}
        {showCostMonitor && activeTab === 'costs' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-theme-silver">Cost Monitoring</h2>
              <button
                onClick={() => databaseService.clearCache()}
                className="btn-secondary flex items-center space-x-2"
              >
                <Database className="h-4 w-4" />
                <span>Clear Cache</span>
              </button>
            </div>
            
            <CostMonitor />
          </div>
        )}

        {/* Settings Tab hidden per request */}

        {/* Create Giveaway Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <h3 className="text-xl font-bold text-theme-silver mb-4">Create New Giveaway</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Title</label>
                    <input
                      type="text"
                      value={newGiveaway.title}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, title: e.target.value }))}
                      className="glass-input"
                      placeholder="Giveaway title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Description</label>
                    <textarea
                      value={newGiveaway.description}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, description: e.target.value }))}
                      className="glass-input"
                      rows="3"
                      placeholder="Giveaway description"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    <label className="block text-sm font-medium text-theme-silver">Giveaway Images</label>
                    <div className="flex items-center gap-3">
                      <input
                        id={giveawayCreateInputId}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const ref = await uploadGiveawayImage(file);
                            setNewGiveaway(prev => {
                              const next = Array.isArray(prev.images) ? [...prev.images, ref] : [ref];
                              return { ...prev, image: prev.image || ref, images: next };
                            });
                          } catch (err) {
                            console.error('Giveaway image upload failed', err);
                            toast.error('Giveaway image upload failed');
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor={giveawayCreateInputId} className="btn-secondary cursor-pointer">
                        Choose Image
                      </label>
                      <input
                        id={giveawayCreateMultiInputId}
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          try {
                            const uploads = [];
                            for (let i = 0; i < files.length; i++) {
                              const f = files[i];
                              if (f) uploads.push(uploadGiveawayImage(f));
                            }
                            const refs = await Promise.all(uploads);
                            setNewGiveaway(prev => {
                              const merged = Array.isArray(prev.images) ? [...prev.images, ...refs] : [...refs];
                              return { ...prev, images: merged, image: prev.image || merged[0] || prev.image };
                            });
                          } catch (err) {
                            console.error('Multiple giveaway image upload failed', err);
                            toast.error('Some giveaway image uploads failed');
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor={giveawayCreateMultiInputId} className="btn-secondary cursor-pointer">
                        Choose Multiple
                      </label>
                    </div>
                    {Array.isArray(newGiveaway.images) && newGiveaway.images.length > 0 ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {newGiveaway.images.map((img, idx) => (
                            <div key={img + idx} className="relative">
                              <S3Image
                                s3KeyOrUrl={img}
                                alt={`Giveaway image ${idx + 1}`}
                                className="w-20 h-20 object-contain glass-card p-1"
                                fallback="/files/Logo.png"
                              />
                              <div className="flex gap-1 mt-1">
                                <button
                                  type="button"
                                  className="btn-secondary text-xs px-2 py-1"
                                  onClick={() => setNewGiveaway(prev => ({ ...prev, image: img }))}
                                >
                                  Set Primary
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary text-xs px-2 py-1"
                                  onClick={() => setNewGiveaway(prev => {
                                    const next = (prev.images || []).filter((x, i) => i !== idx);
                                    const nextPrimary = prev.image === img ? (next[0] || '/files/Logo.png') : prev.image;
                                    return { ...prev, images: next, image: nextPrimary };
                                  })}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <p className="text-xs text-theme-silver-light">
                      {isStorageConfigured() ? 'Uploads use Amplify S3 (public) and set the image reference automatically.' : 'Set REACT_APP_S3_BUCKET to enable S3 uploads.'}
                    </p>
                    <label className="block text-sm font-medium text-theme-silver">Or Image URL / Reference</label>
                    <input
                      className="glass-input"
                      value={newGiveaway.image || ''}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, image: e.target.value }))}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Tier</label>
                      <select
                        value={newGiveaway.tier}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, tier: e.target.value }))}
                        className="glass-input"
                      >
                        <option value="Bronze">Bronze</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold">Gold</option>
                        <option value="Platinum">Platinum</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Value ($)</label>
                      <input
                        type="number"
                        value={newGiveaway.value}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, value: parseInt(e.target.value) }))}
                        className="glass-input"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Total Slots</label>
                    <input
                      type="number"
                      value={newGiveaway.totalSlots}
                      onChange={(e) => setNewGiveaway(prev => ({ ...prev, totalSlots: parseInt(e.target.value) }))}
                      className="glass-input"
                      placeholder="100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Entry Limit Per User</label>
                    <input
                      type="number"
                      value={newGiveaway.entryLimit}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 5 : parseInt(e.target.value) || 5;
                        setNewGiveaway(prev => ({ ...prev, entryLimit: value }));
                      }}
                      className="glass-input"
                      placeholder="5"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-theme-silver/70 mt-1">Maximum entries per user for this giveaway (default: 5)</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Start Date</label>
                      <input
                        type="date"
                        value={newGiveaway.startDate}
                        onChange={(e) => {
                          const startDate = e.target.value;
                          // Automatically calculate early access date (48 hours before start)
                          const earlyAccessDate = startDate ? 
                            calculateEarlyAccessDate(startDate) : 
                            '';
                          setNewGiveaway(prev => ({ 
                            ...prev, 
                            startDate, 
                            earlyAccessDate 
                          }));
                        }}
                        className="glass-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Early Access Date</label>
                      <input
                        type="date"
                        value={newGiveaway.earlyAccessDate}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, earlyAccessDate: e.target.value }))}
                        className="glass-input"
                        title="Automatically calculated as 48 hours before start date"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">End Date</label>
                      <input
                        type="date"
                        value={newGiveaway.endDate}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, endDate: e.target.value }))}
                        className="glass-input"
                      />
                    </div>
                  </div>

                  {/* Email Notification Settings */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-theme-silver">Email Notifications</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sendPremiumEmails"
                        checked={newGiveaway.sendPremiumEmails}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, sendPremiumEmails: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="sendPremiumEmails" className="text-sm font-medium text-theme-silver">
                        Send early access emails to premium users (48 hours before start)
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sendFreeUserEmails"
                        checked={newGiveaway.sendFreeUserEmails}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, sendFreeUserEmails: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="sendFreeUserEmails" className="text-sm font-medium text-theme-silver">
                        Send notification emails to all free users (at start time)
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      Premium users are automatically detected from Stripe subscriptions. 
                      If both checkboxes are unchecked, no emails will be sent for this giveaway.
                    </p>
                  </div>

                  {/* SMS/MMS Notification Settings */}
                  <div className="space-y-4 mt-6">
                    <h4 className="text-lg font-semibold text-theme-silver">SMS/MMS Notifications</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sendPremiumSMS"
                        checked={newGiveaway.sendPremiumSMS}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, sendPremiumSMS: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="sendPremiumSMS" className="text-sm font-medium text-theme-silver">
                        Send early access SMS to premium users (48 hours before start)
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="sendFreeUserSMS"
                        checked={newGiveaway.sendFreeUserSMS}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, sendFreeUserSMS: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="sendFreeUserSMS" className="text-sm font-medium text-theme-silver">
                        Send notification SMS to all free users (at start time)
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      SMS notifications require users to have phone numbers in their profiles. 
                      SMS and email notifications work independently - you can send one, both, or neither.
                    </p>
                  </div>

                  {/* Subscription Visibility Settings */}
                  <div className="space-y-4 mt-6">
                    <h4 className="text-lg font-semibold text-theme-silver">Visibility Settings</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="subscriptionVisibilityOnly"
                        checked={newGiveaway.subscriptionVisibilityOnly}
                        onChange={(e) => setNewGiveaway(prev => ({ ...prev, subscriptionVisibilityOnly: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="subscriptionVisibilityOnly" className="text-sm font-medium text-theme-silver">
                        Only show to subscribed users 48 hours before start date
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      When enabled, this giveaway will only be visible to users with active subscriptions, 
                      and only starting 48 hours before the official start date. Free users will not see 
                      this giveaway at all.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateGiveaway}
                    className="btn-primary flex-1"
                  >
                    Create Giveaway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Product Modal */}
        {showCreateProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <h3 className="text-xl font-bold text-theme-silver mb-4">Create New Product</h3>
                <AdminProductForm onCancel={() => setShowCreateProduct(false)} onSave={async (values) => {
                  const result = await createProduct(values);
                  if (result && result.success) {
                    setShowCreateProduct(false);
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Edit Product Modal */}
        {showEditProduct && selectedProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <h3 className="text-xl font-bold text-theme-silver mb-4">Edit Product</h3>
                <AdminProductForm initialValues={selectedProduct} onCancel={() => setShowEditProduct(false)} onSave={async (values) => {
                  const result = await updateProduct(selectedProduct.id, values);
                  if (result && result.success) {
                    setShowEditProduct(false);
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Edit Giveaway Modal */}
        {showEditForm && selectedGiveaway && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <h3 className="text-xl font-bold text-theme-silver mb-4">Edit Giveaway</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Title</label>
                    <input
                      type="text"
                      value={selectedGiveaway.title}
                      onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, title: e.target.value }))}
                      className="glass-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Description</label>
                    <textarea
                      value={selectedGiveaway.description}
                      onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, description: e.target.value }))}
                      className="glass-input"
                      rows="3"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Tier</label>
                      <select
                        value={selectedGiveaway.tier}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, tier: e.target.value }))}
                        className="glass-input"
                      >
                        <option value="Bronze">Bronze</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold">Gold</option>
                        <option value="Platinum">Platinum</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Value ($)</label>
                      <input
                        type="number"
                        value={selectedGiveaway.value}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, value: parseInt(e.target.value) }))}
                        className="glass-input"
                      />
                    </div>
                  </div>

                  {/* Giveaway Image */}
                  <div className="grid grid-cols-1 gap-2">
                    <label className="block text-sm font-medium text-theme-silver">Giveaway Images</label>
                    <div className="flex items-center gap-3">
                      <input
                        id={giveawayEditInputId}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const ref = await uploadGiveawayImage(file);
                            setSelectedGiveaway(prev => {
                              const next = Array.isArray(prev.images) ? [...prev.images, ref] : [ref];
                              return { ...prev, image: prev.image || ref, images: next };
                            });
                          } catch (err) {
                            console.error('Giveaway image upload failed', err);
                            toast.error('Giveaway image upload failed');
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor={giveawayEditInputId} className="btn-secondary cursor-pointer">
                        Choose Image
                      </label>
                      <input
                        id={giveawayEditMultiInputId}
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          try {
                            const uploads = [];
                            for (let i = 0; i < files.length; i++) {
                              const f = files[i];
                              if (f) uploads.push(uploadGiveawayImage(f));
                            }
                            const refs = await Promise.all(uploads);
                            setSelectedGiveaway(prev => {
                              const merged = Array.isArray(prev.images) ? [...prev.images, ...refs] : [...refs];
                              return { ...prev, images: merged, image: prev.image || merged[0] || prev.image };
                            });
                          } catch (err) {
                            console.error('Multiple giveaway image upload failed', err);
                            toast.error('Some giveaway image uploads failed');
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor={giveawayEditMultiInputId} className="btn-secondary cursor-pointer">
                        Choose Multiple
                      </label>
                    </div>
                    {Array.isArray(selectedGiveaway?.images) && selectedGiveaway.images.length > 0 ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {selectedGiveaway.images.map((img, idx) => (
                            <div key={img + idx} className="relative">
                              <S3Image
                                s3KeyOrUrl={img}
                                alt={`Giveaway image ${idx + 1}`}
                                className="w-20 h-20 object-contain glass-card p-1"
                                fallback="/files/Logo.png"
                              />
                              <div className="flex gap-1 mt-1">
                                <button
                                  type="button"
                                  className="btn-secondary text-xs px-2 py-1"
                                  onClick={() => setSelectedGiveaway(prev => ({ ...prev, image: img }))}
                                >
                                  Set Primary
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary text-xs px-2 py-1"
                                  onClick={() => setSelectedGiveaway(prev => {
                                    const next = (prev.images || []).filter((x, i) => i !== idx);
                                    const nextPrimary = prev.image === img ? (next[0] || '/files/Logo.png') : prev.image;
                                    return { ...prev, images: next, image: nextPrimary };
                                  })}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <label className="block text-sm font-medium text-theme-silver">Or Image URL / Reference</label>
                    <input
                      className="glass-input"
                      value={selectedGiveaway.image || ''}
                      onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, image: e.target.value }))}
                    />
                  </div>

                  {/* Slots (entries capacity) */}
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Total Slots</label>
                      <input
                        type="number"
                        value={selectedGiveaway.totalSlots ?? 0}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, totalSlots: parseInt(e.target.value) }))}
                        className="glass-input"
                        placeholder="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Available Slots</label>
                      <input
                        type="number"
                        value={selectedGiveaway.availableSlots ?? 0}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, availableSlots: parseInt(e.target.value) }))}
                        className="glass-input"
                        placeholder="100"
                      />
                    </div>
                  </div>
                  {isApiConfigured() && (
                    <div className="mt-3 p-3 rounded-lg bg-theme-black/40 border border-theme-silver/20">
                      <p className="text-xs text-theme-silver-light mb-2">
                        If this count looks wrong after editing dates or removing admin-reserved entries, recalculate it from actual slot entries in the database.
                      </p>
                      <button
                        type="button"
                        onClick={handleReconcileSlots}
                        disabled={reconcileSlotsLoading}
                        className="btn-secondary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-4 w-4 ${reconcileSlotsLoading ? 'animate-spin' : ''}`} />
                        Reconcile slot counts
                      </button>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-theme-silver mb-2">Entry Limit Per User</label>
                    <input
                      type="number"
                      value={selectedGiveaway.entryLimit ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value);
                        setSelectedGiveaway(prev => ({ ...prev, entryLimit: value }));
                      }}
                      className="glass-input"
                      placeholder="5"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-theme-silver/70 mt-1">Maximum entries per user for this giveaway (default: 5)</p>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Start Date</label>
                      <input
                        type="date"
                        value={selectedGiveaway.startDate || ''}
                        onChange={(e) => {
                          const startDate = e.target.value;
                          // Automatically calculate early access date (48 hours before start)
                          const earlyAccessDate = startDate ? 
                            calculateEarlyAccessDate(startDate) : 
                            '';
                          setSelectedGiveaway(prev => ({ 
                            ...prev, 
                            startDate, 
                            earlyAccessDate 
                          }));
                        }}
                        className="glass-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">Early Access Date</label>
                      <input
                        type="date"
                        value={selectedGiveaway.earlyAccessDate || ''}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, earlyAccessDate: e.target.value }))}
                        className="glass-input"
                        title="Automatically calculated as 48 hours before start date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-silver mb-2">End Date</label>
                      <input
                        type="date"
                        value={selectedGiveaway.endDate || ''}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, endDate: e.target.value }))}
                        className="glass-input"
                      />
                    </div>
                  </div>

                  {/* Email Notification Settings */}
                  <div className="space-y-4 mt-4">
                    <h4 className="text-lg font-semibold text-theme-silver">Email Notifications</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="editSendPremiumEmails"
                        checked={selectedGiveaway.sendPremiumEmails || false}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, sendPremiumEmails: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="editSendPremiumEmails" className="text-sm font-medium text-theme-silver">
                        Send early access emails to premium users (48 hours before start)
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="editSendFreeUserEmails"
                        checked={selectedGiveaway.sendFreeUserEmails || false}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, sendFreeUserEmails: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="editSendFreeUserEmails" className="text-sm font-medium text-theme-silver">
                        Send notification emails to all free users (at start time)
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      Premium users are automatically detected from Stripe subscriptions. 
                      If both checkboxes are unchecked, no emails will be sent for this giveaway.
                    </p>
                  </div>

                  {/* SMS/MMS Notification Settings */}
                  <div className="space-y-4 mt-6">
                    <h4 className="text-lg font-semibold text-theme-silver">SMS/MMS Notifications</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="editSendPremiumSMS"
                        checked={selectedGiveaway.sendPremiumSMS || false}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, sendPremiumSMS: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="editSendPremiumSMS" className="text-sm font-medium text-theme-silver">
                        Send early access SMS to premium users (48 hours before start)
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="editSendFreeUserSMS"
                        checked={selectedGiveaway.sendFreeUserSMS || false}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, sendFreeUserSMS: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="editSendFreeUserSMS" className="text-sm font-medium text-theme-silver">
                        Send notification SMS to all free users (at start time)
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      SMS notifications require users to have phone numbers in their profiles. 
                      SMS and email notifications work independently - you can send one, both, or neither.
                    </p>
                  </div>

                  {/* Subscription Visibility Settings */}
                  <div className="space-y-4 mt-6">
                    <h4 className="text-lg font-semibold text-theme-silver">Visibility Settings</h4>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="editSubscriptionVisibilityOnly"
                        checked={selectedGiveaway.subscriptionVisibilityOnly || false}
                        onChange={(e) => setSelectedGiveaway(prev => ({ ...prev, subscriptionVisibilityOnly: e.target.checked }))}
                        className="w-4 h-4 text-theme-bronze bg-theme-black border-theme-silver rounded focus:ring-theme-bronze focus:ring-2"
                      />
                      <label htmlFor="editSubscriptionVisibilityOnly" className="text-sm font-medium text-theme-silver">
                        Only show to subscribed users 48 hours before start date
                      </label>
                    </div>
                    
                    <p className="text-xs text-theme-silver-light">
                      When enabled, this giveaway will only be visible to users with active subscriptions, 
                      and only starting 48 hours before the official start date. Free users will not see 
                      this giveaway at all.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditForm(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateGiveaway}
                    className="btn-primary flex-1"
                  >
                    Update Giveaway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Giveaway Entries Modal */}
        {showEntriesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-theme-silver">Giveaway Entries</h3>
                  <button
                    onClick={() => setShowEntriesModal(false)}
                    className="text-theme-silver-light hover:text-theme-silver"
                  >
                    ✕
                  </button>
                </div>
                
                {entriesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                    <span className="ml-3 text-theme-silver">Loading entries...</span>
                  </div>
                ) : selectedGiveawayEntries.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <p className="text-theme-silver-light">
                          Total Entries: {selectedGiveawayEntries.length}
                        </p>
                        <p className="text-theme-silver-light text-sm">
                          {selectedGiveaway?.status === 'completed' || selectedGiveaway?.winner
                            ? 'Winner has been selected. Cancel to select a new winner.'
                            : 'Click "Select Winner" to automatically select a random winner'}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* AI CONTEXT: admin-functionality - Winner visibility controls */}
                        {/* Reasoning: Allow admins to hide winner for configurable days */}
                        {/* Journal: J-20260112-01 */}
                        {selectedGiveaway?.status === 'completed' || selectedGiveaway?.winner ? (
                          <>
                            <button
                              onClick={async () => {
                                const days = window.prompt('Hide winner for how many days? (Enter 0 to show immediately)', '7');
                                if (days === null) return; // User canceled
                                
                                try {
                                  setEntriesLoading(true);
                                  const hideDays = parseInt(days) || 0;
                                  const response = await authorizedFetch('/admin/set-winner-visibility', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ 
                                      giveawayId: selectedGiveaway.id,
                                      hideDays: hideDays
                                    })
                                  });
                                  if (response) {
                                    if (hideDays > 0) {
                                      toast.success(`Winner hidden for ${hideDays} day(s)`);
                                      setSelectedGiveaway(prev => prev ? { 
                                        ...prev, 
                                        hideWinnerUntil: Date.now() + (hideDays * 24 * 60 * 60 * 1000)
                                      } : null);
                                    } else {
                                      toast.success('Winner is now visible to all users');
                                      setSelectedGiveaway(prev => prev ? { 
                                        ...prev, 
                                        hideWinnerUntil: null
                                      } : null);
                                    }
                                    await fetchGiveaways();
                                  }
                                } catch (error) {
                                  console.error('Error setting winner visibility:', error);
                                  toast.error(error.message || 'Failed to set winner visibility');
                                } finally {
                                  setEntriesLoading(false);
                                }
                              }}
                              disabled={entriesLoading}
                              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
                              title={selectedGiveaway?.hideWinnerUntil 
                                ? `Winner hidden until ${new Date(selectedGiveaway.hideWinnerUntil).toLocaleDateString()}`
                                : 'Winner is currently visible'
                              }
                            >
                              <Eye className="h-5 w-5" />
                              {selectedGiveaway?.hideWinnerUntil && Date.now() < selectedGiveaway.hideWinnerUntil 
                                ? 'Winner Hidden' 
                                : 'Hide Winner'}
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Are you sure you want to cancel the current winner for "${selectedGiveaway.title}"? This will allow you to select a new winner.`)) {
                                  return;
                                }
                                try {
                                  setEntriesLoading(true);
                                  const response = await authorizedFetch('/admin/cancel-winner', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ giveawayId: selectedGiveaway.id })
                                  });
                                  if (response) {
                                    toast.success('Winner canceled successfully. You can now select a new winner.');
                                    // Update selectedGiveaway immediately to reflect the change
                                    setSelectedGiveaway(prev => prev ? { ...prev, status: null, winner: null } : null);
                                    // Refresh giveaways to update the list
                                    await fetchGiveaways();
                                  }
                                } catch (error) {
                                  console.error('Error canceling winner:', error);
                                  toast.error(error.message || 'Failed to cancel winner');
                                } finally {
                                  setEntriesLoading(false);
                                }
                              }}
                              disabled={entriesLoading}
                              className="bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
                            >
                              <Trash2 className="h-5 w-5" />
                              Cancel Winner
                            </button>
                          </>
                        ) : null}
                        <button
                          onClick={() => setShowPrivateEntryModal(true)}
                          disabled={entriesLoading}
                          className="bg-theme-bronze hover:bg-theme-bronze/80 disabled:bg-theme-bronze/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
                        >
                          <UserPlus className="h-5 w-5" />
                          Add Private Entry
                        </button>
                        {isApiConfigured() && (
                          <button
                            type="button"
                            onClick={handleReconcileSlots}
                            disabled={entriesLoading || reconcileSlotsLoading}
                            title="Recalculate available slots from ENTRY rows in the database"
                            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
                          >
                            <RefreshCw className={`h-5 w-5 ${reconcileSlotsLoading ? 'animate-spin' : ''}`} />
                            Reconcile slots
                          </button>
                        )}
                        <button
                          onClick={() => setShowWinnerPickerModal(true)}
                          disabled={entriesLoading || selectedGiveawayEntries.length === 0 || selectedGiveaway?.status === 'completed' || selectedGiveaway?.winner}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-600/50 disabled:to-pink-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-lg"
                        >
                          <Gift className="h-5 w-5" />
                          Pick Winner (Animated)
                        </button>
                        <button
                          onClick={handleAutomaticWinnerSelection}
                          disabled={entriesLoading || selectedGiveaway?.status === 'completed' || selectedGiveaway?.winner}
                          className="bg-custom-orange hover:bg-custom-orange/80 disabled:bg-custom-orange/50 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
                        >
                          <Gift className="h-5 w-5" />
                          Select Winner
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {selectedGiveawayEntries.map((entry, index) => {
                        // AI CONTEXT: admin-functionality - Highlight winner entry in entries modal
                        // Reasoning: Allow admins to easily identify the selected winner when viewing entries
                        // Constraints: Only visible to admins, matches by slot number and email
                        // Journal: J-20251226-01
                        const isWinner = selectedGiveaway?.winner && 
                          String(entry.slotNumber) === String(selectedGiveaway.winner.slot) &&
                          entry.email?.toLowerCase() === selectedGiveaway.winner.email?.toLowerCase();
                        const isAdminFilled =
                          (entry.email || '').toLowerCase() === 'admin@tolberttactical.com' ||
                          entry.name === 'Reserved by Admin' ||
                          (entry.tier === 'Reserved' && entry.isPrivateEntry);
                        
                        return (
                        <div
                          key={`${entry.email}-${entry.slotNumber}-${index}`}
                          className={`glass-card p-4 border ${
                            isWinner 
                              ? 'border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20 ring-2 ring-green-500/50' 
                              : isAdminFilled
                                ? 'border-green-600 bg-green-600/15 ring-1 ring-green-600/40'
                              : entry.isPrivateEntry 
                                ? 'border-theme-bronze/50 bg-theme-bronze/5' 
                                : 'border-theme-silver/20'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className={`text-lg font-bold ${isWinner || isAdminFilled ? 'text-green-400' : 'text-custom-orange'}`}>
                              Slot {entry.slotNumber}
                            </span>
                            <div className="flex items-center gap-2">
                              {isWinner && (
                                <span className="text-xs bg-green-500 text-white px-2 py-1 rounded font-bold animate-pulse">
                                  🏆 Winner
                                </span>
                              )}
                              {isAdminFilled && !isWinner && (
                                <span className="text-xs bg-green-600 text-white px-2 py-1 rounded font-bold">
                                  Admin filled
                                </span>
                              )}
                              {entry.isPrivateEntry && !isAdminFilled && (
                                <span className="text-xs bg-theme-bronze/20 text-theme-bronze px-2 py-1 rounded">
                                  Private
                                </span>
                              )}
                              <span className={`tier-badge tier-${(entry.tier || 'Bronze').toLowerCase()}`}>
                                {entry.tier || 'Bronze'}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {entry.name ? (
                              <div className="text-center mb-3">
                                <p className="text-lg font-bold text-theme-silver">
                                  {entry.name}
                                </p>
                                <p className="text-xs text-theme-silver-light">
                                  {entry.isPrivateEntry ? 'Private Entry' : 'Participant'}
                                </p>
                              </div>
                            ) : (
                              <div className="text-center mb-3">
                                <p className="text-sm text-theme-silver-light italic">
                                  No name available
                                </p>
                              </div>
                            )}
                            <div className="space-y-1 text-sm">
                              <p className="text-theme-silver font-medium truncate">
                                {entry.email}
                              </p>
                              <p className="text-theme-silver-light">
                                {formatDate(entry.date)}
                              </p>
                            </div>
                            <div className="mt-3 pt-3 border-t border-theme-silver/10">
                              <button
                                onClick={() => handleRemoveEntry(entry)}
                                disabled={entriesLoading}
                                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white text-sm font-medium py-2 px-3 rounded transition-colors duration-200 flex items-center justify-center gap-2"
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove Entry
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Gift className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-theme-silver mb-2">No Entries Found</h3>
                    <p className="text-theme-silver-light">This giveaway has no entries yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Winner Picker Modal */}
        {showWinnerPickerModal && selectedGiveaway && (
          <WinnerPickerModal
            giveaway={selectedGiveaway}
            entries={selectedGiveawayEntries}
            isOpen={showWinnerPickerModal}
            onClose={() => {
              setShowWinnerPickerModal(false);
            }}
            onWinnerSelected={async (winner) => {
              // Refresh giveaways to show updated status
              await fetchGiveaways();
              
              // Refresh winners if on winners tab
              if (activeTab === 'winners') {
                await loadWinners();
              }
              
              // Toast notification removed per user request
              
              // Optionally close the entries modal as well
              setShowEntriesModal(false);
            }}
          />
        )}

        {/* User Profile Modal */}
        {showUserProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="glass-card-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'none' }}
              onWheel={stopScrollPropagation}
              onTouchMove={stopScrollPropagation}
            >
              <div
                className="p-6 max-h-[80vh] overflow-y-auto"
                style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-6 w-6 text-custom-orange" />
                    <h3 className="text-xl font-bold text-theme-silver">User Profile</h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserProfileModal(false);
                      setSelectedUserProfile(null);
                    }}
                    className="text-theme-silver-light hover:text-theme-silver"
                  >
                    ✕
                  </button>
                </div>
                
                {profileLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-theme-silver animate-spin" />
                    <span className="ml-3 text-theme-silver">Loading profile...</span>
                  </div>
                ) : selectedUserProfile ? (
                  <div className="space-y-6">
                    {/* User Email */}
                    <div>
                      <h4 className="text-lg font-semibold text-theme-silver mb-2">Email</h4>
                      <p className="text-theme-silver-light">{selectedUserProfile.email}</p>
                    </div>

                    {/* Phone Number (Cognito / list data; always show row) */}
                    <div>
                      <h4 className="text-lg font-semibold text-theme-silver mb-2">Phone Number</h4>
                      <p className="text-theme-silver-light">
                        {String(
                          selectedUserProfile.phone ||
                            selectedUserProfile.phone_number ||
                            ''
                        ).trim() || 'Not on file'}
                      </p>
                    </div>

                    {/* SMS Opt-in Status */}
                    <div>
                      <h4 className="text-lg font-semibold text-theme-silver mb-2">SMS Notifications</h4>
                      <div className="flex items-center space-x-2">
                        {selectedUserProfile.attributes?.['custom:sms_opt_in'] === 'true' ? (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-green-400">Opted In</span>
                          </>
                        ) : (
                          <>
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-red-400">Not Opted In</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="text-lg font-semibold text-theme-silver mb-4 flex items-center space-x-2">
                        <MapPin className="h-5 w-5 text-custom-orange" />
                        <span>Shipping Address</span>
                      </h4>
                      
                      {selectedUserProfile.shippingAddress && Object.keys(selectedUserProfile.shippingAddress).length > 0 ? (
                        <div className="glass-card p-4 space-y-3">
                          {selectedUserProfile.shippingAddress.line1 && (
                            <div>
                              <p className="text-sm font-medium text-theme-silver-light">Address Line 1</p>
                              <p className="text-theme-silver">{selectedUserProfile.shippingAddress.line1}</p>
                            </div>
                          )}
                          
                          {selectedUserProfile.shippingAddress.line2 && (
                            <div>
                              <p className="text-sm font-medium text-theme-silver-light">Address Line 2</p>
                              <p className="text-theme-silver">{selectedUserProfile.shippingAddress.line2}</p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            {selectedUserProfile.shippingAddress.city && (
                              <div>
                                <p className="text-sm font-medium text-theme-silver-light">City</p>
                                <p className="text-theme-silver">{selectedUserProfile.shippingAddress.city}</p>
                              </div>
                            )}
                            
                            {selectedUserProfile.shippingAddress.state && (
                              <div>
                                <p className="text-sm font-medium text-theme-silver-light">State</p>
                                <p className="text-theme-silver">{selectedUserProfile.shippingAddress.state}</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {selectedUserProfile.shippingAddress.postalCode && (
                              <div>
                                <p className="text-sm font-medium text-theme-silver-light">Postal Code</p>
                                <p className="text-theme-silver">{selectedUserProfile.shippingAddress.postalCode}</p>
                              </div>
                            )}
                            
                            {selectedUserProfile.shippingAddress.country && (
                              <div>
                                <p className="text-sm font-medium text-theme-silver-light">Country</p>
                                <p className="text-theme-silver">{selectedUserProfile.shippingAddress.country}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="glass-card p-4 text-center">
                          <MapPin className="h-8 w-8 text-theme-silver-light mx-auto mb-2" />
                          <p className="text-theme-silver-light">No shipping address on file</p>
                        </div>
                      )}
                    </div>

                    {/* Last Updated */}
                    {selectedUserProfile.updatedAt && (
                      <div>
                        <h4 className="text-lg font-semibold text-theme-silver mb-2">Last Updated</h4>
                        <p className="text-theme-silver-light">{formatDate(selectedUserProfile.updatedAt)}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-theme-silver mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-theme-silver mb-2">Profile Not Found</h3>
                    <p className="text-theme-silver-light">This user's profile could not be loaded.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Private Entry Modal */}
        <UserCreationModal
          isOpen={showPrivateEntryModal}
          onClose={() => setShowPrivateEntryModal(false)}
          onSubmit={handleCreatePrivateEntry}
          title="Create Private Entry"
          submitText="Create Private Entry"
          loading={privateEntryLoading}
        />

        {/* User Creation Modal */}
        <UserCreationModal
          isOpen={showUserCreationModal}
          onClose={() => setShowUserCreationModal(false)}
          onSubmit={handleCreateUser}
          title="Create New User"
          submitText="Create User"
          loading={userCreationLoading}
        />
      </div>

      {/* SMS Modal */}
      <AdminSmsModal
        isOpen={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        selectedUsers={selectedUsers}
        allUsers={allUsers.length > 0 ? allUsers : users}
      />
    </div>
  );
};

export default Admin; 